import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { useRazorpay } from "react-razorpay";
import { motion } from "framer-motion";
import { QRCodeSVG } from "react-qr-code";
import { Html5QrcodeScanner } from "html5-qrcode";
import { ShoppingCart, User, LogOut, Ticket, MessageCircle, X, Send, Menu, Plus, Edit, Trash2, Package, Tag, ShoppingBag, Home as HomeIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { AdminMfaGate } from "@/components/admin/AdminMfaGate";
import { AdminSettingsPanel } from "@/components/admin/AdminSettingsPanel";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Uploaded product images are stored as backend-relative paths (/api/uploads/...).
const resolveImageUrl = (url) => (url && url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);

const downloadCsv = async (path, params, filename) => {
  const response = await axios.get(`${API}${path}`, { params, withCredentials: true, responseType: "blob" });
  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const getCookieValue = (name) => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};

axios.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  const csrfToken = getCookieValue("csrf_token") || getCookieValue("operator_csrf_token") || getCookieValue("park_csrf_token");
  if (["post", "put", "patch", "delete"].includes(method) && csrfToken) {
    config.headers = {
      ...(config.headers || {}),
      "X-CSRF-Token": csrfToken,
    };
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Cart Context
const CartContext = createContext(null);

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  const addToCart = (product, quantity = 1) => {
    const existingItem = cart.find(item => item.product_id === product.product_id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.product_id === product.product_id
          ? { ...item, quantity: item.quantity + quantity }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity }]);
    }
    toast.success(`${product.name} added to cart!`);
  };

  const removeFromCart = (product_id) => {
    setCart(cart.filter(item => item.product_id !== product_id));
    toast.info("Item removed from cart");
  };

  const updateQuantity = (product_id, quantity) => {
    if (quantity <= 0) {
      removeFromCart(product_id);
      return;
    }
    setCart(cart.map(item =>
      item.product_id === product_id ? { ...item, quantity } : item
    ));
  };

  const clearCart = () => {
    setCart([]);
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + (item.final_price * item.quantity), 0);
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, getTotal }}>
      {children}
    </CartContext.Provider>
  );
};

const useCart = () => useContext(CartContext);

// Header Component
const Header = () => {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="glass-effect sticky top-0 z-50 border-b border-white/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <Link to="/" className="flex min-w-0 items-center space-x-2 whitespace-nowrap">
            <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_friendlytickets-app/artifacts/sfpetbfd_Friendship%20Ad_20251224_102947_0000.png" alt="Friendship Ad logo" className="h-12 w-12 rounded-full object-cover" data-testid="friendship-ad-logo" />
            <div className="whitespace-nowrap text-2xl font-black text-[#0EA5E9] tracking-tight sm:text-3xl" style={{ fontFamily: 'Nunito' }} data-testid="friendship-ad-wordmark">
              Friendship Ad
            </div>
          </Link>

          {/* Desktop Menu */}
          <nav className="hidden xl:flex items-center space-x-4">
            <Link to="/" className="text-[#0F172A] hover:text-[#0EA5E9] font-semibold">Home</Link>
            <Link to="/park" className="text-[#0F172A] hover:text-[#0EA5E9] font-semibold" data-testid="park-page-link">Park</Link>
            <Link to="/owner" className="text-[#0F172A] hover:text-[#0EA5E9] font-semibold" data-testid="topbar-owner-workspace-link">Owner</Link>
            <Link to="/chief-admin" className="text-[#0F172A] hover:text-[#0EA5E9] font-semibold" data-testid="topbar-chief-workspace-link">Chief</Link>
            <Link to="/contact" className="text-[#0F172A] hover:text-[#0EA5E9] font-semibold" data-testid="topbar-contact-link">Contact Us</Link>
            <Link to="/advertise" className="text-[#0F172A] hover:text-[#0EA5E9] font-semibold" data-testid="topbar-advertise-link">Advertise</Link>
            <Link to="/admin" className="text-[#0F172A] hover:text-[#0EA5E9] font-semibold" data-testid="topbar-admin-workspace-link">Admin</Link>
            <Link to="/agent-workspace" className="text-[#0F172A] hover:text-[#0EA5E9] font-semibold" data-testid="topbar-agent-workspace-link">Agent</Link>
            {user && <Link to="/my-orders" className="text-[#0F172A] hover:text-[#0EA5E9] font-semibold" data-testid="my-orders-link">My Orders</Link>}
            {user?.admin_role === "owner" && <Link to="/owner" className="text-[#0F172A] hover:text-[#0EA5E9] font-semibold" data-testid="owner-admin-link">Owner Portal</Link>}
            <button
              onClick={() => navigate('/cart')}
              className="relative p-2 hover:bg-white/50 rounded-full"
              data-testid="cart-button"
            >
              <ShoppingCart className="w-6 h-6 text-[#0EA5E9]" />
              {cartCount > 0 && (
                <Badge className="absolute -top-1 -right-1 bg-[#F43F5E] text-white w-5 h-5 flex items-center justify-center p-0 cart-badge-animate">
                  {cartCount}
                </Badge>
              )}
            </button>
            {user ? (
              <div className="flex items-center space-x-3">
                <span className="text-sm text-[#475569]">Hi, {user.name}</span>
                <Button onClick={logout} variant="ghost" size="sm" data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            ) : (
              <Button onClick={() => navigate('/login')} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="login-button">
                <User className="w-4 h-4 mr-2" />
                Login
              </Button>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="xl:hidden p-2"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="xl:hidden pb-4 space-y-3"
          >
            <Link to="/" className="block py-2 text-[#0F172A] hover:text-[#0EA5E9]">Home</Link>
            <Link to="/park" className="block py-2 text-[#0F172A] hover:text-[#0EA5E9]" data-testid="mobile-park-page-link">Park</Link>
            <Link to="/owner" className="block py-2 text-[#0F172A] hover:text-[#0EA5E9]" data-testid="mobile-owner-workspace-link">Owner</Link>
            <Link to="/chief-admin" className="block py-2 text-[#0F172A] hover:text-[#0EA5E9]" data-testid="mobile-chief-workspace-link">Chief</Link>
            <Link to="/admin" className="block py-2 text-[#0F172A] hover:text-[#0EA5E9]" data-testid="mobile-admin-workspace-link">Admin</Link>
            <Link to="/agent-workspace" className="block py-2 text-[#0F172A] hover:text-[#0EA5E9]" data-testid="mobile-agent-workspace-link">Agent</Link>
            <Link to="/contact" className="block py-2 text-[#0F172A] hover:text-[#0EA5E9]" data-testid="mobile-contact-link">Contact Us</Link>
            <Link to="/advertise" className="block py-2 text-[#0F172A] hover:text-[#0EA5E9]" data-testid="mobile-advertise-link">Advertise</Link>
            {user && <Link to="/my-orders" className="block py-2 text-[#0F172A] hover:text-[#0EA5E9]" data-testid="mobile-my-orders-link">My Orders</Link>}
            {user?.admin_role === "owner" && <Link to="/owner" className="block py-2 text-[#0F172A] hover:text-[#0EA5E9]" data-testid="mobile-owner-admin-link">Owner Portal</Link>}
            <button
              onClick={() => navigate('/cart')}
              className="flex items-center py-2 text-[#0F172A] hover:text-[#0EA5E9]"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Cart {cartCount > 0 && `(${cartCount})`}
            </button>
            {user ? (
              <button onClick={logout} className="flex items-center py-2 text-[#0F172A]">
                <LogOut className="w-5 h-5 mr-2" />
                Logout
              </button>
            ) : (
              <button onClick={() => navigate('/login')} className="flex items-center py-2 text-[#0EA5E9]">
                <User className="w-5 h-5 mr-2" />
                Login
              </button>
            )}
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

const SiteFooter = () => (
  <footer className="border-t border-slate-200 bg-white px-4 py-8" data-testid="site-footer">
    <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 text-sm text-[#475569]">
      <span>© {new Date().getFullYear()} Friendship Ad</span>
      <span className="font-semibold text-[#0F172A]" data-testid="site-credit">Made by Paul architect</span>
    </div>
  </footer>
);

const ContactUs = () => <div className="min-h-screen bg-slate-50 px-4 py-16" data-testid="contact-page"><div className="mx-auto max-w-4xl"><h1 className="text-4xl font-black text-[#0F172A]" data-testid="contact-page-title">Contact Us</h1><p className="mt-3 text-[#475569]" data-testid="contact-page-intro">Friendship Ad helps families discover and book Happy Valley Park ticket options in one clear, mobile-friendly place.</p><div className="mt-8 grid gap-5 sm:grid-cols-2"><a href="tel:9830089616" className="border-l-4 border-[#0EA5E9] bg-white p-6 shadow-sm" data-testid="contact-phone-link"><p className="text-sm font-bold text-[#0EA5E9]">PHONE</p><p className="mt-2 text-xl font-black text-[#0F172A]" data-testid="contact-phone-number">9830089616</p></a><a href="mailto:contact@friendshipad.com" className="border-l-4 border-[#FBBF24] bg-white p-6 shadow-sm" data-testid="contact-email-link"><p className="text-sm font-bold text-[#B45309]">EMAIL</p><p className="mt-2 break-all text-xl font-black text-[#0F172A]" data-testid="contact-email-address">contact@friendshipad.com</p></a></div><section className="mt-10" data-testid="about-friendship-ad-section"><h2 className="text-2xl font-black text-[#0F172A]">About Friendship Ad</h2><p className="mt-3 text-[#475569]">Choose amusement and water-park tickets, use available offers, and prepare for QR-ready entry in one simple booking flow.</p><div className="mt-5 grid gap-4 md:grid-cols-3"><div className="bg-white p-5 shadow-sm" data-testid="about-advantage-choice"><h3 className="font-bold">Clear choices</h3><p className="mt-2 text-sm text-[#475569]">Browse ticket types and prices before booking.</p></div><div className="bg-white p-5 shadow-sm" data-testid="about-advantage-qr"><h3 className="font-bold">QR-ready entry</h3><p className="mt-2 text-sm text-[#475569]">Verified bookings create an entry pass.</p></div><div className="bg-white p-5 shadow-sm" data-testid="about-advantage-support"><h3 className="font-bold">Helpful support</h3><p className="mt-2 text-sm text-[#475569]">Contact Friendship Ad for booking help.</p></div></div></section></div></div>;

// Chatbot Component
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`chat_${Date.now()}`);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: "user", content: input };
    setMessages([...messages, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: input,
        session_id: sessionId
      });
      setMessages(prev => [...prev, { role: "assistant", content: response.data.message }]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm having trouble connecting right now." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-full p-4 shadow-xl btn-hover-lift z-50"
        data-testid="chatbot-toggle"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-24 right-6 w-96 h-[500px] bg-white rounded-3xl shadow-2xl flex flex-col z-50"
        >
          <div className="bg-[#0EA5E9] text-white p-4 rounded-t-3xl">
            <h3 className="font-bold text-lg">Chat Support</h3>
            <p className="text-xs text-white/80">Ask us anything!</p>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center text-[#475569] py-8">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-[#0EA5E9]" />
                <p className="text-sm">Hi! How can I help you today?</p>
              </div>
            )}
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-2xl ${msg.role === 'user' ? 'bg-[#0EA5E9] text-white' : 'bg-gray-100 text-[#0F172A]'}`}>
                  <p className="text-sm">{msg.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 p-3 rounded-2xl">
                  <p className="text-sm text-[#475569]">Typing...</p>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t">
            <div className="flex space-x-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Type a message..."
                className="flex-1"
                data-testid="chat-input"
              />
              <Button onClick={sendMessage} disabled={loading} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="chat-send">
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

// Home Page
const AdStrip = () => {
  const [ads, setAds] = useState([]);
  const [idx, setIdx] = useState(0);
  useEffect(() => { axios.get(`${API}/ads/active`).then((r) => setAds(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (ads.length < 2) return; const t = setInterval(() => setIdx((i) => (i + 1) % ads.length), 6000); return () => clearInterval(t); }, [ads]);
  if (!ads.length) return null;
  const ad = ads[idx % ads.length];
  return (
    <section className="bg-[#0B1120] px-4 py-3" data-testid="paid-ad-strip">
      <div className="mx-auto flex max-w-7xl items-center gap-4">
        <span className="shrink-0 text-xs font-black tracking-wide text-[#38BDF8]">ADVERTISEMENT</span>
        <a href={ad.destination_url || "#"} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-white min-w-0 transition-opacity duration-500" data-testid="paid-ad-banner">
          <img src={resolveImageUrl(ad.banner_url)} alt={ad.business_name} className="h-12 w-24 rounded object-cover" />
          <span className="truncate font-semibold">{ad.business_name}</span>
        </a>
        <Link to="/advertise" className="ml-auto shrink-0 text-xs font-semibold text-[#94A3B8] hover:text-white" data-testid="paid-ad-cta">Advertise here →</Link>
      </div>
    </section>
  );
};

const AdvertisePage = () => {
  const { Razorpay } = useRazorpay();
  const [form, setForm] = useState({ plan: "monthly", business_name: "", email: "", phone: "", destination_url: "" });
  const [banner, setBanner] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const onFile = (e) => { const f = e.target.files?.[0]; if (!f) return; if (f.size > 3 * 1024 * 1024) return toast.error("Banner must be under 3 MB."); const r = new FileReader(); r.onload = () => setBanner(r.result); r.readAsDataURL(f); };
  const submit = async () => {
    if (!form.business_name.trim() || !form.email.trim()) return toast.error("Enter business name and email.");
    if (!banner) return toast.error("Upload your small rectangle banner image.");
    if (!Razorpay) return toast.error("Payment library is still loading. Try again in a moment.");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/ads/purchase/create`, { ...form, banner_image: banner, daily_minutes: 25 });
      const d = res.data;
      const opts = {
        key: d.key_id, name: "Friendship Ad", description: form.plan === "monthly" ? "Monthly Advertisement (autopay · 5-year term)" : "5-Year Advertisement",
        prefill: { name: form.business_name, email: form.email, contact: form.phone },
        handler: async (payment) => {
          try {
            const vr = await axios.post(`${API}/ads/purchase/verify`, { ad_id: d.ad_id, razorpay_payment_id: payment.razorpay_payment_id, razorpay_signature: payment.razorpay_signature, razorpay_order_id: payment.razorpay_order_id, razorpay_subscription_id: payment.razorpay_subscription_id });
            setDone(vr.data); toast.success("Your advertisement is now live on the website!");
          } catch (err) { toast.error(err.response?.data?.detail || "Payment could not be verified."); }
        },
      };
      if (d.mode === "subscription") opts.subscription_id = d.subscription_id; else { opts.order_id = d.order_id; opts.amount = d.amount; }
      new Razorpay(opts).open();
    } catch (e) { toast.error(e.response?.data?.detail || "Unable to start advertisement checkout."); }
    finally { setBusy(false); }
  };
  if (done) return <div className="min-h-screen py-16 px-4"><div className="max-w-xl mx-auto"><Card className="neumorphic-card rounded-3xl p-8 text-center" data-testid="advertise-success"><h1 className="text-3xl font-black text-emerald-700">Advertisement live 🎉</h1><p className="mt-3 text-[#475569]">Your rectangle banner now rotates in the website's 2nd row.</p><a href={`${BACKEND_URL}${done.invoice_url}`} target="_blank" rel="noreferrer"><Button className="mt-6 bg-[#0F766E]" data-testid="advertise-invoice-button">Download invoice</Button></a><div className="mt-3"><Link to="/" className="text-[#0EA5E9] font-semibold">Back to home</Link></div></Card></div></div>;
  return <div className="min-h-screen py-12 px-4"><div className="max-w-2xl mx-auto"><Card className="neumorphic-card rounded-3xl p-8" data-testid="advertise-page">
    <h1 className="text-4xl font-black text-[#0F172A]">Advertise with Friendship Ad</h1>
    <p className="mt-2 text-[#475569]">Your small rectangle banner rotates in the website's 2nd row, up to 25 minutes of air-time a day. Fill in your details below.</p>
    <div className="mt-6 grid gap-3">
      <div className="grid gap-3 md:grid-cols-2">
        <label className={`cursor-pointer rounded-2xl border p-4 ${form.plan === "monthly" ? "border-[#0F766E] bg-emerald-50" : "border-slate-200"}`} data-testid="advertise-plan-monthly"><input type="radio" name="plan" checked={form.plan === "monthly"} onChange={() => setForm({ ...form, plan: "monthly" })} className="mr-2" /><strong>₹10,000 / month</strong><div className="text-xs text-[#475569]">Autopay via Razorpay · 5-year minimum term</div></label>
        <label className={`cursor-pointer rounded-2xl border p-4 ${form.plan === "term5y" ? "border-[#0F766E] bg-emerald-50" : "border-slate-200"}`} data-testid="advertise-plan-term"><input type="radio" name="plan" checked={form.plan === "term5y"} onChange={() => setForm({ ...form, plan: "term5y" })} className="mr-2" /><strong>₹4,50,000 one-time</strong><div className="text-xs text-[#475569]">5 years · no autopay</div></label>
      </div>
      <Input placeholder="Business / advertiser name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="advertise-business-name" />
      <Input placeholder="Email (for invoice)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="advertise-email" />
      <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="advertise-phone" />
      <Input placeholder="Banner click-through link (optional)" value={form.destination_url} onChange={(e) => setForm({ ...form, destination_url: e.target.value })} data-testid="advertise-url" />
      <div><Label>Rectangle banner image (PNG/JPG/WEBP, under 3 MB)</Label><input type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="mt-1 block w-full text-sm" data-testid="advertise-banner-file" />{banner && <img src={banner} alt="Banner preview" className="mt-2 h-16 w-32 rounded object-cover" data-testid="advertise-banner-preview" />}</div>
      <Button onClick={submit} disabled={busy || !Razorpay} className="bg-[#0F766E]" data-testid="advertise-pay-button">{busy ? "Starting…" : form.plan === "monthly" ? "Start autopay — ₹10,000/month" : "Pay ₹4,50,000 for 5 years"}</Button>
    </div>
  </Card></div></div>;
};


const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState("West Bengal");
  const [searchQuery, setSearchQuery] = useState("");
  const [siteContent, setSiteContent] = useState({ banners: [], reels: [] });
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
        const content = await axios.get(`${API}/site-content`);
        setSiteContent(content.data);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Failed to load products");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);
  const indiaStates = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal"];
  const visibleProducts = products.filter((product) => `${product.name} ${product.description}`.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative h-[600px] overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/9f59c9f3-5e7f-40a6-abd6-30863dece052/images/de28936d8d11187da72ccc42a7468ce3e6c9fb2d21e930d118c94ee0b9a8e848.png')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0EA5E9]/90 to-[#0EA5E9]/50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 h-full flex items-center">
          <div className="max-w-2xl">
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight mb-6"
              style={{ fontFamily: 'Nunito' }}
            >
              Welcome To Friendship Ad
            </motion.h1>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-white/90 mb-8 leading-relaxed"
            >
              Experience unlimited fun with water slides, adventure rides, and family entertainment. Book your tickets now and enjoy 20% off!
            </motion.p>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <Button
                onClick={() => document.getElementById('tickets').scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] text-lg px-8 py-6 rounded-full font-bold btn-hover-lift"
                data-testid="get-started-btn"
              >
                <Ticket className="w-5 h-5 mr-2" />
                Book Tickets Now
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.section>

      <AdStrip />
      <section className="bg-[#0F172A] px-4 py-3" data-testid="sponsor-ribbon-section"><div className="mx-auto flex max-w-7xl items-center gap-4 overflow-hidden"><span className="shrink-0 text-xs font-black tracking-wide text-[#FBBF24]" data-testid="sponsor-ribbon-label">SPONSORED</span><div className="flex min-w-0 flex-1 gap-4 animate-[pulse_5s_ease-in-out_infinite]">{(siteContent.banners?.length ? siteContent.banners : [{ title: "Happy Valley Amusement Park — rides, water fun & family days", image_url: "https://images.unsplash.com/photo-1553603227-2358b3e4c4b2?auto=format&fit=crop&w=900&q=80" }]).slice(0, 3).map((banner, index) => <a key={`${banner.title}-${index}`} href={banner.destination_url || "#tickets"} className="flex min-w-full items-center gap-3 text-white" data-testid={`sponsor-ribbon-banner-${index}`}><img src={resolveImageUrl(banner.image_url)} alt={banner.title} className="h-12 w-20 object-cover"/><span className="font-semibold">{banner.title}</span></a>)}</div></div></section>

      <section className="bg-white px-4 py-10" data-testid="park-discovery-section">
        <div className="mx-auto grid max-w-7xl gap-4 md:grid-cols-[0.9fr_1.1fr]">
          <div><Label htmlFor="india-state-select">Choose your state</Label><select id="india-state-select" value={selectedState} onChange={(event) => setSelectedState(event.target.value)} className="mt-2 flex h-11 w-full rounded-md border border-slate-300 bg-white px-3" data-testid="india-state-select">{indiaStates.map((state) => <option key={state} value={state}>{state}</option>)}</select></div>
          <div><Label htmlFor="park-search">Search tickets and attractions</Label><Input id="park-search" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search water park, adult, child…" className="mt-2" data-testid="park-search-input" /></div>
        </div>
        {selectedState !== "West Bengal" && <div className="mx-auto mt-5 max-w-7xl border-l-4 border-[#FBBF24] bg-[#FFFBEB] px-5 py-4 font-semibold text-[#92400E]" data-testid="state-coming-soon-message">{selectedState} is unavailable presently. Coming soon.</div>}
        {selectedState === "West Bengal" && <p className="mx-auto mt-4 max-w-7xl text-sm font-semibold text-[#0F766E]" data-testid="west-bengal-park-availability">Happy Valley Park is currently available in West Bengal.</p>}
      </section>

      {/* Products Section */}
      <section id="tickets" className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0F172A] mb-4" style={{ fontFamily: 'Nunito' }}>
              Choose Your Adventure
            </h2>
            <p className="text-[#475569] text-lg">Get 20% off on all tickets!</p>
          </motion.div>

          {selectedState !== "West Bengal" ? <div className="py-12 text-center text-[#475569]" data-testid="state-unavailable-ticket-placeholder">Tickets will appear here when this state opens.</div> : loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="bg-white rounded-3xl h-96"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {visibleProducts.map((product, idx) => (
                <motion.div
                  key={product.product_id}
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className="neumorphic-card rounded-3xl overflow-hidden border-0 h-full" data-testid={`product-card-${product.product_id}`}>
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={resolveImageUrl(product.image_url)}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                      <Badge className="absolute top-3 right-3 bg-[#F43F5E] text-white px-3 py-1 discount-pulse">
                        {product.discount_percent}% OFF
                      </Badge>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-xl font-bold text-[#0F172A]">{product.name}</CardTitle>
                      <CardDescription className="text-sm text-[#475569]">{product.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center space-x-3 mb-4">
                        <span className="text-2xl font-black text-[#0EA5E9]">₹{product.final_price}</span>
                        <span className="text-sm text-[#475569] line-through">₹{product.mrp}</span>
                      </div>
                      <Button
                        onClick={() => addToCart(product, 1)}
                        className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-full font-bold btn-hover-lift"
                        data-testid={`add-to-cart-${product.product_id}`}
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Add to Cart
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "Instant Tickets", desc: "Get your tickets via email instantly after payment" },
              { title: "Secure QR Codes", desc: "Each ticket has a unique QR code for entry" },
              { title: "24/7 Support", desc: "Our chatbot is always here to help you" }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center p-8"
              >
                <div className="w-16 h-16 bg-[#0EA5E9]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Ticket className="w-8 h-8 text-[#0EA5E9]" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-2">{feature.title}</h3>
                <p className="text-[#475569]">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="hidden" aria-hidden="true"></section>

      <section className="hidden" data-testid="home-access-section" aria-hidden="true">
        <div className="max-w-5xl mx-auto grid gap-6 md:grid-cols-2">
          <div className="border-l-4 border-[#0EA5E9] bg-white p-8 shadow-sm">
            <p className="text-sm font-bold text-[#0EA5E9]">PARK TEAM</p>
            <h2 className="mt-2 text-2xl font-black text-[#0F172A]">Park</h2>
            <Button onClick={() => navigate('/park')} className="mt-5 bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="home-park-access-button">Open Park</Button>
          </div>
          <div className="border-l-4 border-[#FBBF24] bg-white p-8 shadow-sm">
            <p className="text-sm font-bold text-[#B45309]">OWNER ACCESS</p>
            <h2 className="mt-2 text-2xl font-black text-[#0F172A]">Owner Portal</h2>
            <Button onClick={() => navigate('/owner')} className="mt-5 bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A]" data-testid="home-owner-portal-button">Open Owner Portal</Button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto grid gap-6 md:grid-cols-3 mt-6"><Button variant="outline" onClick={() => navigate('/chief-admin')} data-testid="home-chief-admin-button">Chief</Button><Button variant="outline" onClick={() => navigate('/admin')} data-testid="home-admin-button">Admin</Button><Button variant="outline" onClick={() => navigate('/agent-workspace')} data-testid="home-agent-workspace-button">Agent</Button></div>
      </section>
      <section className="bg-slate-50 px-4 py-16" data-testid="home-ai-reels-section"><div className="mx-auto max-w-7xl"><h2 className="text-2xl font-extrabold text-[#0F172A]" data-testid="home-ai-reels-title">Park moments</h2><p className="mt-2 text-[#475569]">A glimpse of the fun waiting at Happy Valley Park.</p><div className="mt-6 flex gap-4 overflow-x-auto pb-2" data-testid="home-ai-reels-strip">{siteContent.reels?.slice(0, 5).map((reel, index) => <article key={reel.media_id} className="w-64 shrink-0 overflow-hidden rounded-lg bg-white shadow-sm" data-testid={`home-ai-reel-${index}`}><img src={resolveImageUrl(reel.image_url)} alt={reel.title || "Happy Valley Park reel"} className="aspect-[4/5] w-full object-cover"/><p className="p-3 text-sm font-semibold text-[#0F172A]">{reel.title || "Happy Valley Park"}</p></article>)}{!siteContent.reels?.length && <p className="py-10 text-[#475569]" data-testid="home-ai-reels-empty">New park moments are being prepared.</p>}</div></div></section>
    </div>
  );
};

// Cart Page
const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getTotal, clearCart } = useCart();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-black text-[#0F172A] mb-8" style={{ fontFamily: 'Nunito' }} data-testid="cart-title">
          Shopping Cart
        </h1>

        {cart.length === 0 ? (
          <Card className="neumorphic-card rounded-3xl p-12 text-center">
            <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-[#0EA5E9]" />
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Your cart is empty</h2>
            <p className="text-[#475569] mb-6">Add some tickets to get started!</p>
            <Button onClick={() => navigate('/')} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="continue-shopping">
              Continue Shopping
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            {cart.map((item) => (
              <Card key={item.product_id} className="neumorphic-card rounded-3xl" data-testid={`cart-item-${item.product_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-6">
                    <img src={resolveImageUrl(item.image_url)} alt={item.name} className="w-24 h-24 object-cover rounded-xl" />
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[#0F172A]">{item.name}</h3>
                      <p className="text-sm text-[#475569]">{item.description}</p>
                      <p className="text-lg font-bold text-[#0EA5E9] mt-2">₹{item.final_price}</p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          data-testid={`decrease-qty-${item.product_id}`}
                        >
                          -
                        </Button>
                        <span className="w-12 text-center font-bold">{item.quantity}</span>
                        <Button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          data-testid={`increase-qty-${item.product_id}`}
                        >
                          +
                        </Button>
                      </div>
                      <Button
                        onClick={() => removeFromCart(item.product_id)}
                        variant="ghost"
                        size="sm"
                        className="text-[#F43F5E]"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <Card className="neumorphic-card rounded-3xl p-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center text-lg">
                  <span className="text-[#475569]">Subtotal:</span>
                  <span className="font-bold text-[#0F172A]" data-testid="cart-total">₹{getTotal().toFixed(2)}</span>
                </div>
                <Button
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-lg py-6 rounded-full font-bold btn-hover-lift"
                  data-testid="proceed-to-checkout"
                >
                  Proceed to Checkout
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// Checkout Page
const Checkout = () => {
  const { cart, getTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponApplying, setCouponApplying] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setCustomerName((value) => value || user.name || "");
      setCustomerEmail((value) => value || user.email || "");
    }
  }, [user]);

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Enter a coupon code first.");
      return;
    }
    setCouponApplying(true);
    try {
      const response = await axios.post(`${API}/coupons/validate`, { code: couponCode.trim() });
      setCouponCode(response.data.code);
      setCouponDiscount(response.data.discount_percent);
      toast.success("Coupon applied.");
    } catch (error) {
      setCouponDiscount(0);
      toast.error(error.response?.data?.detail || "This coupon is not valid.");
    } finally {
      setCouponApplying(false);
    }
  };

  const handleCheckout = async () => {
    if (!customerName || !customerEmail) {
      toast.error("Please fill in all fields");
      return;
    }

    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        items: cart.map(item => ({ product_id: item.product_id, quantity: item.quantity })),
        coupon_code: couponDiscount > 0 ? couponCode : null,
        customer_email: customerEmail,
        customer_name: customerName
      };

      const response = await axios.post(`${API}/orders`, orderData, { withCredentials: true });
      const orderId = response.data.order_id;
      
      clearCart();
      navigate(`/payment/${orderId}`);
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Failed to create order");
    } finally {
      setLoading(false);
    }
  };

  const discountPercent = couponDiscount > 0 ? couponDiscount : 2.5;
  const finalTotal = getTotal() * (1 - discountPercent / 100);

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-black text-[#0F172A] mb-8" style={{ fontFamily: 'Nunito' }}>
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <Card className="neumorphic-card rounded-3xl p-6">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Customer Details</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Enter your name"
                    className="mt-2"
                    data-testid="checkout-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="mt-2"
                    data-testid="checkout-email"
                  />
                </div>
              </div>
            </Card>
          </div>

          <div>
            <Card className="neumorphic-card rounded-3xl p-6">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Order Summary</h2>
              <div className="space-y-3 mb-6">
                {cart.map((item) => (
                  <div key={item.product_id} className="flex justify-between text-sm">
                    <span className="text-[#475569]">{item.name} x {item.quantity}</span>
                    <span className="font-semibold">₹{(item.final_price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t pt-5">
                <Label htmlFor="private-coupon-code" className="text-[#0F172A]">Coupon code</Label>
                <div className="mt-2 flex gap-2">
                  <Input
                    id="private-coupon-code"
                    value={couponCode}
                    onChange={(event) => { setCouponCode(event.target.value.toUpperCase()); setCouponDiscount(0); }}
                    placeholder="Enter a coupon code"
                    autoComplete="off"
                    data-testid="checkout-coupon-code-input"
                  />
                  <Button type="button" onClick={applyCoupon} disabled={couponApplying} variant="outline" data-testid="apply-checkout-coupon-button">
                    {couponApplying ? "Applying…" : "Apply"}
                  </Button>
                </div>
                {couponDiscount > 0 && <p className="mt-2 text-sm font-semibold text-green-700" data-testid="checkout-coupon-applied">Coupon applied: {couponDiscount}% off</p>}
              </div>
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-[#475569]">Subtotal:</span>
                  <span className="font-semibold">₹{getTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#F43F5E]" data-testid="checkout-discount">
                  <span>Discount ({discountPercent}%):</span>
                  <span>-₹{(getTotal() * discountPercent / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-[#0EA5E9] pt-2 border-t">
                  <span>Total:</span>
                  <span data-testid="checkout-total">₹{finalTotal.toFixed(2)}</span>
                </div>
              </div>
              <Button
                onClick={handleCheckout}
                disabled={loading}
                className="w-full mt-6 bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-lg py-6 rounded-full font-bold btn-hover-lift"
                data-testid="place-order-button"
              >
                {loading ? "Processing..." : "Place Order"}
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payment Page
const Payment = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const { Razorpay } = useRazorpay();
  const openRazorpay = async () => {
    setConfirming(true);
    try {
      const created = await axios.post(`${API}/orders/${orderId}/razorpay/create`);
      new Razorpay({ key: created.data.key_id, amount: created.data.amount, currency: "INR", name: "Friendship Ad", description: `Tickets ${orderId}`, order_id: created.data.order_id, handler: async (payment) => { await axios.post(`${API}/orders/${orderId}/razorpay/verify`, payment); setOrderStatus("completed"); navigate(`/tickets/${orderId}`); } }).open();
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to start Razorpay payment."); } finally { setConfirming(false); }
  };
  const payAgentOrder = async () => {
    setConfirming(true);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/agent/checkout`, {}, { withCredentials: true });
      if (res.data.fully_paid) { setOrderStatus("completed"); toast.success("Paid from wallet. Ticket issued."); navigate(`/tickets/${orderId}`); return; }
      new Razorpay({ key: res.data.key_id, amount: res.data.amount, currency: "INR", name: "Friendship Ad", description: `Agent sale ${orderId}`, order_id: res.data.order_id, handler: async (payment) => { await axios.post(`${API}/orders/${orderId}/razorpay/verify`, payment, { withCredentials: true }); setOrderStatus("completed"); navigate(`/tickets/${orderId}`); } }).open();
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to start agent checkout."); } finally { setConfirming(false); }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orderRes, paymentRes] = await Promise.all([
          axios.get(`${API}/orders/${orderId}`),
          axios.get(`${API}/payment/info`)
        ]);
        setOrder(orderRes.data);
        setPaymentInfo(paymentRes.data);
        setOrderStatus(orderRes.data.payment_status);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoadError(error.response?.data?.detail || "We could not load this payment request.");
        toast.error("Failed to load payment information");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orderId]);

  const checkOrderStatus = async () => {
    setChecking(true);
    try {
      const response = await axios.get(`${API}/orders/${orderId}/status`);
      setOrderStatus(response.data.payment_status);
      
      if (response.data.payment_status === 'completed' && response.data.tickets_generated) {
        toast.success("Payment verified! Redirecting to your tickets...");
        setTimeout(() => navigate(`/tickets/${orderId}`), 1500);
      } else if (response.data.payment_status === 'rejected') {
        toast.error("Payment verification failed. Please contact support.");
      }
    } catch (error) {
      console.error("Status check error:", error);
    } finally {
      setChecking(false);
    }
  };

  const handleScreenshotUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const supportedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!supportedTypes.includes(file.type.toLowerCase())) {
      toast.error("Please upload a PNG, JPG, or WEBP screenshot");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setScreenshot(reader.result);
      setPreviewUrl(reader.result);
      toast.success("Screenshot uploaded!");
    };
    reader.readAsDataURL(file);
  };

  const [aiResult, setAiResult] = useState(null);
  const confirmPayment = async () => {
    if (!screenshot) {
      toast.error("Please upload payment screenshot first");
      return;
    }

    setConfirming(true);
    try {
      const response = await axios.post(`${API}/orders/${orderId}/confirm`, {
        payment_screenshot: screenshot
      });
      
      setAiResult(response.data);
      
      if (response.data.status === 'completed' && response.data.verification === 'auto_approved') {
        setOrderStatus('completed');
        toast.success(`🤖 Gemini AI verified! Your entry pass is ready now.`);
        navigate(`/tickets/${orderId}`);
      } else if (response.data.status === 'rejected') {
        setOrderStatus('rejected');
        toast.error("❌ Verification failed - see details below");
      } else {
        setOrderStatus(response.data.status);
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error("Payment confirmation error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit payment");
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-xl">Loading...</div></div>;
  }

  if (loadError || !order || !paymentInfo) {
    return (
      <div className="min-h-screen py-12 px-4 flex items-center justify-center">
        <Card className="neumorphic-card rounded-3xl p-8 max-w-lg w-full text-center" data-testid="payment-load-error">
          <h1 className="text-3xl font-black text-[#0F172A]" style={{ fontFamily: 'Nunito' }}>Payment request unavailable</h1>
          <p className="mt-3 text-[#475569]" data-testid="payment-load-error-message">{loadError || "Please return to checkout and create a new order."}</p>
          <Button onClick={() => navigate('/checkout')} className="mt-6 bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="payment-return-checkout-button">Return to checkout</Button>
        </Card>
      </div>
    );
  }

  if (orderStatus === 'pending' && order.is_agent_sale) return <div className="min-h-screen py-12 px-4"><div className="max-w-xl mx-auto"><Card className="neumorphic-card rounded-3xl p-8 text-center" data-testid="agent-sale-payment-card"><h1 className="text-3xl font-black text-[#0F172A]">Agent sale checkout</h1><p className="mt-3 text-[#475569]">Customer invoice (MRP): <strong>₹{Number(order.invoice_total ?? order.subtotal).toFixed(2)}</strong></p><p className="mt-1 text-[#0F766E] font-semibold">Your cost after {order.agent_discount_percent}% membership discount: ₹{Number(order.total).toFixed(2)}</p><p className="mt-2 text-sm text-[#475569]">We use your wallet first, then Razorpay for any shortfall.</p><Button onClick={payAgentOrder} disabled={!Razorpay || confirming} className="mt-6 bg-[#0F766E]" data-testid="agent-pay-button">{confirming ? 'Processing…' : 'Pay now (wallet + Razorpay)'}</Button></Card></div></div>;

  if (orderStatus === 'pending') return <div className="min-h-screen py-12 px-4"><div className="max-w-xl mx-auto"><Card className="neumorphic-card rounded-3xl p-8 text-center" data-testid="razorpay-payment-card"><h1 className="text-3xl font-black text-[#0F172A]">Pay securely with Razorpay</h1><p className="mt-3 text-[#475569]">₹{Number(order.total).toFixed(2)} · Tickets are issued only after Razorpay confirms payment.</p><Button onClick={openRazorpay} disabled={!Razorpay || confirming} className="mt-7 bg-[#0F766E]" data-testid="open-razorpay-checkout-button">{confirming ? 'Opening Razorpay…' : 'Pay with Razorpay'}</Button></Card></div></div>;

  if (orderStatus === 'verification_in_progress') {
    return (
      <div className="min-h-screen py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="neumorphic-card rounded-3xl p-8 text-center" data-testid="verification-in-progress-card">
            <div className="w-16 h-16 bg-[#FBBF24] rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse" data-testid="verification-in-progress-indicator"><Ticket className="w-8 h-8 text-white" /></div>
            <h1 className="text-3xl font-black text-[#0F172A] mb-2" data-testid="verification-in-progress-title">Verification in Progress</h1>
            <p className="text-[#475569]" data-testid="verification-in-progress-message">Gemini is temporarily busy. Your order has not been rejected and no ticket has been issued.</p>
            <Button onClick={confirmPayment} disabled={confirming || !screenshot} className="mt-6 bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="retry-gemini-verification-button">{confirming ? "Retrying…" : "Retry Same Screenshot"}</Button>
          </Card>
        </div>
      </div>
    );
  }

  // Show pending manual review screen
  if (orderStatus === 'pending_verification') {
    return (
      <div className="min-h-screen py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
            <Card className="neumorphic-card rounded-3xl p-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-[#FBBF24] rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h1 className="text-3xl font-black text-[#0F172A] mb-2" style={{ fontFamily: 'Nunito' }}>
                  Manual Review in Progress
                </h1>
                <p className="text-[#475569] mb-6">Order ID: {orderId}</p>
                <div className="bg-[#FFF9E6] border-2 border-[#FBBF24] p-6 rounded-2xl mb-6 text-left">
                  <p className="text-[#0F172A] font-semibold mb-2">✓ Your request has been submitted</p>
                  <p className="text-sm text-[#475569] mb-3">Our team is manually reviewing your payment.</p>
                  <ul className="text-sm text-[#475569] space-y-2">
                    <li>⏱️ Review time: 5-30 minutes</li>
                    <li>📧 You&apos;ll receive an email once approved</li>
                    <li>📱 Tickets will be sent to your email</li>
                  </ul>
                </div>
                <Button onClick={checkOrderStatus} disabled={checking} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="check-status-button">
                  {checking ? "Checking..." : "🔄 Check Status"}
                </Button>
                <p className="text-xs text-[#475569] mt-4">Need help? <a href="mailto:support@friendshipad.com" className="text-[#0EA5E9] underline">support@friendshipad.com</a></p>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show rejection screen
  if (orderStatus === 'rejected') {
    const aiDetails = aiResult?.ai_details || {};
    const reason = aiResult?.reason || order?.rejection_reason || "Payment verification failed";
    const customerReasons = aiResult?.customer_reasons || order?.customer_rejection_reasons || [reason];
    const confidence = aiResult?.confidence || 0;
    const isDuplicate = aiResult?.verification === 'duplicate_detected';
    
    return (
      <div className="min-h-screen py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
            <Card className="neumorphic-card rounded-3xl p-8">
              <div className="text-center mb-6">
                <div className={`w-20 h-20 ${isDuplicate ? 'bg-orange-500' : 'bg-[#F43F5E]'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  {isDuplicate ? (
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  ) : (
                    <X className="w-10 h-10 text-white" />
                  )}
                </div>
                <h1 className="text-3xl font-black text-[#0F172A] mb-2" style={{ fontFamily: 'Nunito' }}>
                  {isDuplicate ? "Duplicate Screenshot Detected" : "Verification Failed"}
                </h1>
                <p className="text-[#475569]">Order ID: {orderId}</p>
                {confidence > 0 && !isDuplicate && (
                  <Badge className="mt-2 bg-orange-500">
                    Confidence: {(confidence * 100).toFixed(0)}%
                  </Badge>
                )}
              </div>

              {/* Duplicate Warning */}
              {isDuplicate && (
                <div className="bg-orange-50 border-2 border-orange-400 p-5 rounded-2xl mb-4">
                  <h3 className="font-bold text-orange-900 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    This Screenshot Was Already Used!
                  </h3>
                  <div className="text-sm text-orange-900 bg-white p-4 rounded-lg border border-orange-300">
                    <p className="font-semibold mb-2">{reason}</p>
                    <p className="text-xs mt-3 text-orange-700">⚠️ Each order requires a unique payment screenshot. Using the same screenshot twice is not allowed.</p>
                  </div>
                </div>
              )}

              {/* AI Detailed Reason (only show if not duplicate) */}
              {!isDuplicate && (
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 p-5 rounded-2xl mb-4">
                  <h3 className="font-bold text-purple-900 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    AI Verification Result:
                  </h3>
                  <p className="text-sm text-purple-900 font-medium bg-white p-3 rounded-lg border border-purple-200">
                    &quot;{reason}&quot;
                  </p>
                </div>
              )}

              {!isDuplicate && customerReasons.length > 0 && (
                <div className="bg-[#FFF7ED] border border-[#FDBA74] p-5 rounded-2xl mb-4" data-testid="payment-rejection-reasons">
                  <h3 className="font-bold text-[#9A3412] mb-2" data-testid="payment-rejection-reasons-title">What needs to be corrected</h3>
                  <ul className="space-y-2 text-sm text-[#9A3412]" data-testid="payment-rejection-reasons-list">
                    {customerReasons.map((item, index) => <li key={`${item}-${index}`} data-testid={`payment-rejection-reason-${index}`}>• {item}</li>)}
                  </ul>
                </div>
              )}

              {/* 3 Check Details (only show if not duplicate) */}
              {!isDuplicate && aiDetails && Object.keys(aiDetails).length > 0 && (
                <div className="bg-[#FFE5E5] border-2 border-[#F43F5E] p-5 rounded-2xl mb-6">
                  <h3 className="font-bold text-[#F43F5E] mb-3">3-Check Verification:</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center p-2 bg-white rounded">
                      <span className="font-semibold text-[#0F172A]">1. Amount Match (₹{order.total.toFixed(2)}):</span>
                      <span className={aiDetails.amount_verified ? "text-green-600 font-bold" : "text-red-600 font-bold"}>
                        {aiDetails.amount_verified ? "✓ Verified" : "✗ Not Found"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-white rounded">
                      <span className="font-semibold text-[#0F172A]">2. UPI ID Match:</span>
                      <span className={aiDetails.upi_verified ? "text-green-600 font-bold" : "text-red-600 font-bold"}>
                        {aiDetails.upi_verified ? "✓ Verified" : "✗ Not Found"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-white rounded">
                      <span className="font-semibold text-[#0F172A]">3. Recipient &quot;Friendship Ad&quot;:</span>
                      <span className={aiResult?.analysis?.name_matches_friendship_ad ? "text-green-600 font-bold" : "text-red-600 font-bold"}>
                        {aiResult?.analysis?.name_matches_friendship_ad ? "✓ Verified" : "✗ Not Found"}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-[#F43F5E] mt-3 text-center font-semibold" data-testid="strict-payment-requirements">
                    Successful payment, exact amount, and exact UPI ID are required
                  </p>
                </div>
              )}

              <div className="bg-[#FFFBEB] border-2 border-[#FBBF24] p-5 rounded-2xl mb-6">
                <h3 className="font-bold text-[#0F172A] mb-3">Expected Payment Details:</h3>
                <div className="space-y-1 text-sm">
                  <p><span className="font-semibold">Amount:</span> ₹{order.total.toFixed(2)}</p>
                  <p><span className="font-semibold">UPI ID:</span> {(paymentInfo.upi_ids || [paymentInfo.upi_id]).join(" or ")}</p>
                  <p><span className="font-semibold">Recipient:</span> Friendship Ad</p>
                </div>
              </div>

              <div className="space-y-3">
                <Button onClick={() => window.location.reload()} className="bg-[#0EA5E9] hover:bg-[#0284C7] w-full text-lg py-6" data-testid="retry-payment-button">
                  📸 Upload New Screenshot
                </Button>
                <p className="text-sm text-center text-[#475569]">Need help? <a href="mailto:support@friendshipad.com" className="text-[#0EA5E9] font-semibold underline">Contact Support</a></p>
              </div>

            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  if (orderStatus === 'completed') {
    const aiDetails = aiResult?.ai_details || {};
    const reason = aiResult?.reason || "Payment successfully verified";
    const confidence = aiResult?.confidence || 0;
    
    if (aiResult) {
      // Show approval confirmation with AI details for a few seconds
      return (
        <div className="min-h-screen py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
              <Card className="neumorphic-card rounded-3xl p-8">
                <div className="text-center mb-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200 }}
                    className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4"
                  >
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </motion.div>
                  <h1 className="text-3xl font-black text-[#0F172A] mb-2" style={{ fontFamily: 'Nunito' }}>
                    Payment Verified! 🎉
                  </h1>
                  <p className="text-[#475569]">Order ID: {orderId}</p>
                  <Badge className="mt-2 bg-green-600">
                    AI Confidence: {(confidence * 100).toFixed(0)}%
                  </Badge>
                </div>

                {/* AI Reason for Approval */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 p-5 rounded-2xl mb-4">
                  <h3 className="font-bold text-green-900 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    AI Verification Reason:
                  </h3>
                  <p className="text-sm text-green-900 font-medium bg-white p-3 rounded-lg border border-green-200">
                    &quot;{reason}&quot;
                  </p>
                </div>

                {/* Verification Details */}
                <div className="bg-[#F0F9FF] border border-[#0EA5E9] p-5 rounded-2xl mb-6">
                  <h3 className="font-bold text-[#0F172A] mb-3">✓ Verified Details:</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center p-2 bg-white rounded">
                      <span className="font-semibold">Amount Match:</span>
                      <span className="text-green-600 font-bold">✓ ₹{order.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-white rounded">
                      <span className="font-semibold">UPI Match:</span>
                      <span className="text-green-600 font-bold">✓ Verified</span>
                    </div>
                    <div className="flex justify-between items-center p-2 bg-white rounded">
                      <span className="font-semibold">Transaction:</span>
                      <span className="text-green-600 font-bold capitalize">✓ {aiDetails.transaction_status || 'Success'}</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => navigate(`/tickets/${orderId}`)}
                  className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-lg py-6 rounded-full font-bold btn-hover-lift"
                  data-testid="view-tickets-button"
                >
                  🎫 View My Tickets
                </Button>

                <p className="text-center text-sm text-[#475569] mt-4" data-testid="ticket-delivery-status">
                  Your entry pass is ready now. An email confirmation is being sent to <span className="font-semibold">{order.customer_email}</span>.
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      );
    }
    
    // Direct navigation if no AI result (returning visitor)
    navigate(`/tickets/${orderId}`);
    return null;
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
          <Card className="neumorphic-card rounded-3xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#FBBF24] rounded-full flex items-center justify-center mx-auto mb-4">
                <Ticket className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-black text-[#0F172A] mb-2" style={{ fontFamily: 'Nunito' }}>
                Complete Payment
              </h1>
              <p className="text-[#475569]">Order ID: {orderId}</p>
            </div>

            <div className="bg-[#F0F9FF] p-6 rounded-2xl mb-6">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">Payment Instructions</h2>
              <div className="space-y-3 text-[#475569]">
                <p><strong>Step 1:</strong> Transfer <span className="font-bold text-[#0EA5E9]">₹{order.total.toFixed(2)}</span> to the UPI ID below</p>
                <div className="bg-white p-4 rounded-xl border-2 border-[#0EA5E9]">
                  <p className="text-sm text-[#475569] mb-1">UPI ID:</p>
                  {(paymentInfo.upi_ids || [paymentInfo.upi_id]).map((upiId, index) => (
                    <p key={upiId} className="text-2xl font-black text-[#0EA5E9]" data-testid={`upi-id-${index}`}>{upiId}</p>
                  ))}
                  <p className="text-xs text-[#475569] mt-2">Amount: ₹{order.total.toFixed(2)}</p>
                  <p className="text-xs text-[#475569] mt-1">Recipient: <span className="font-semibold">Friendship Ad</span></p>
                </div>
                <p><strong>Step 2:</strong> Take a screenshot showing payment success</p>
                <p><strong>Step 3:</strong> Upload screenshot - AI verifies instantly! 🤖</p>
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-3 rounded-lg border border-purple-200 mt-3">
                  <p className="text-sm font-semibold text-purple-900">⚡ AI Verification - 3 Simple Checks:</p>
                  <ul className="text-xs text-purple-700 mt-2 space-y-1">
                    <li>1. ✓ Amount: ₹{order.total.toFixed(2)}</li>
                    <li>2. ✓ UPI ID: {(paymentInfo.upi_ids || [paymentInfo.upi_id]).join(" or ")}</li>
                    <li>3. ✓ Recipient Name: Friendship Ad</li>
                  </ul>
                  <p className="text-xs text-purple-600 mt-2 italic" data-testid="payment-verification-requirements">Gemini requires a successful, original receipt with the exact amount, approved UPI ID, recipient name, transaction date, and UTR/reference.</p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <Label htmlFor="screenshot" className="text-lg font-bold text-[#0F172A] mb-3 block">
                Upload Payment Screenshot *
              </Label>
              <div className="border-2 border-dashed border-[#0EA5E9] rounded-2xl p-6 text-center hover:bg-[#F0F9FF] transition-colors">
                <input
                  type="file"
                  id="screenshot"
                  accept="image/*"
                  onChange={handleScreenshotUpload}
                  className="hidden"
                  data-testid="screenshot-upload"
                />
                <label htmlFor="screenshot" className="cursor-pointer">
                  {previewUrl ? (
                    <div className="space-y-3">
                      <img src={previewUrl} alt="Payment screenshot" className="max-h-64 mx-auto rounded-xl" />
                      <p className="text-sm text-green-600 font-semibold">✓ Screenshot uploaded</p>
                      <Button type="button" variant="outline" size="sm">Change Screenshot</Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-16 h-16 bg-[#0EA5E9]/10 rounded-full flex items-center justify-center mx-auto">
                        <svg className="w-8 h-8 text-[#0EA5E9]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <p className="text-[#0F172A] font-semibold">Click to upload payment screenshot</p>
                  <p className="text-xs text-[#475569]">PNG, JPG, or WEBP up to 5MB</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            <Button
              onClick={confirmPayment}
              disabled={confirming || !screenshot}
              className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-lg py-6 rounded-full font-bold btn-hover-lift disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="confirm-payment-button"
            >
              {confirming ? "Submitting..." : "Submit for Verification"}
            </Button>

            <p className="text-center text-sm text-[#475569] mt-4">
              Tickets will be sent to <span className="font-semibold">{order.customer_email}</span> after verification
            </p>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

// Tickets Page
const Tickets = () => {
  const { orderId } = useParams();
  const [tickets, setTickets] = useState([]);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const [ticketsRes, orderRes] = await Promise.all([
          axios.get(`${API}/tickets/${orderId}`),
          axios.get(`${API}/orders/${orderId}`)
        ]);
        setTickets(ticketsRes.data);
        setOrder(orderRes.data);
      } catch (error) {
        console.error("Error fetching tickets:", error);
        toast.error("Failed to load tickets");
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, [orderId]);

  const downloadInvoice = () => {
    window.open(`${API}/invoice/${orderId}`, '_blank');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-xl">Loading...</div></div>;
  }

  const ticketQuantity = tickets.length;
  const entryPass = tickets[0];

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-[#0EA5E9] rounded-full flex items-center justify-center mx-auto mb-4">
              <Ticket className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-black text-[#0F172A] mb-2" style={{ fontFamily: 'Nunito' }}>
              Your Tickets Are Ready!
            </h1>
            <p className="text-[#475569] text-lg">Order #{orderId}</p>
            <p className="text-sm text-[#475569] mt-2">Tickets have been sent to {order?.customer_email}</p>
            <Button
              onClick={downloadInvoice}
              variant="outline"
              className="mt-4"
              data-testid="download-invoice"
            >
              Download Invoice (PDF)
            </Button>
          </div>

          {entryPass && (
            <Card className="neumorphic-card rounded-3xl overflow-hidden mb-6" data-testid="one-time-entry-pass">
              <div className="bg-[#0F172A] p-6 text-white">
                <h2 className="text-2xl font-bold">One-Time Entry Pass</h2>
                <p className="text-white/80">This single QR pass becomes invalid after entry.</p>
              </div>
              <CardContent className="grid gap-6 p-8 md:grid-cols-2 md:items-center">
                <div className="space-y-3">
                  <p data-testid="entry-pass-order-id"><span className="text-sm text-[#475569]">Order ID</span><br /><strong>{orderId}</strong></p>
                  <p data-testid="entry-pass-quantity"><span className="text-sm text-[#475569]">Tickets quantity</span><br /><strong>{ticketQuantity}</strong></p>
                  <p data-testid="entry-pass-amount"><span className="text-sm text-[#475569]">Amount before discount</span><br /><strong>₹{Number(order?.subtotal || 0).toFixed(2)}</strong></p>
                  <p className="text-sm text-[#475569]">Show this pass at entry. Staff will see your order ID and ticket quantity after scanning.</p>
                </div>
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-2xl shadow-lg">
                    <img src={`data:image/png;base64,${entryPass.qr_code}`} alt="One-time entry QR code" className="h-56 w-56" data-testid="one-time-entry-qr" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-6">
            {tickets.map((ticket, idx) => (
              <motion.div
                key={ticket.ticket_id}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="neumorphic-card rounded-3xl overflow-hidden" data-testid={`ticket-${ticket.ticket_id}`}>
                  <div className="bg-gradient-to-r from-[#0EA5E9] to-[#0284C7] p-6 text-white">
                    <h2 className="text-2xl font-bold">{ticket.product_name}</h2>
                    <p className="text-white/80 text-sm">Ticket ID: {ticket.ticket_id}</p>
                  </div>
                  <CardContent className="p-8">
                    <div className="grid grid-cols-1 gap-8 items-center">
                      <div>
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm text-[#475569]">Customer Name</p>
                            <p className="font-bold text-[#0F172A]">{ticket.customer_name}</p>
                          </div>
                          <div>
                            <p className="text-sm text-[#475569]">Valid Until</p>
                            <p className="font-bold text-[#0F172A]">
                              {new Date(ticket.valid_until).toLocaleDateString()}
                            </p>
                          </div>
                          <p className="text-sm text-[#0F172A]">Covered by the one-time entry pass above.</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button
              onClick={() => window.location.href = '/'}
              className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
              data-testid="back-to-home"
            >
              <HomeIcon className="w-5 h-5 mr-2" />
              Back to Home
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const CustomerOrderHistory = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    const loadOrders = async () => {
      try {
        const response = await axios.get(`${API}/customer/orders`, { withCredentials: true });
        setOrders(response.data);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Unable to load your orders.");
      } finally {
        setLoading(false);
      }
    };
    loadOrders();
  }, [authLoading, navigate, user]);

  if (loading || authLoading) return <div className="min-h-screen flex items-center justify-center" data-testid="order-history-loading">Loading your orders…</div>;

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl font-black text-[#0F172A]" style={{ fontFamily: "Nunito" }} data-testid="order-history-title">My Orders</h1>
        <p className="mt-2 text-[#475569]" data-testid="order-history-customer-email">{user.email}</p>
        {orders.length === 0 ? (
          <Card className="neumorphic-card rounded-3xl mt-8 p-10 text-center" data-testid="order-history-empty">
            <Ticket className="w-12 h-12 mx-auto text-[#0EA5E9] mb-4" />
            <p className="text-[#475569]">You do not have any orders yet.</p>
            <Button className="mt-5 bg-[#0EA5E9] hover:bg-[#0284C7]" onClick={() => navigate("/")} data-testid="order-history-book-button">Book tickets</Button>
          </Card>
        ) : (
          <div className="mt-8 space-y-4">
            {orders.map((order) => (
              <Card key={order.order_id} className="neumorphic-card rounded-3xl" data-testid={`history-order-${order.order_id}`}>
                <CardContent className="flex flex-wrap items-center justify-between gap-5 p-6">
                  <div>
                    <p className="font-bold text-[#0F172A]" data-testid={`history-order-id-${order.order_id}`}>Order {order.order_id}</p>
                    <p className="text-sm text-[#475569]">{new Date(order.created_at).toLocaleDateString()} · {order.items.reduce((sum, item) => sum + item.quantity, 0)} ticket(s)</p>
                    <p className="mt-2 text-sm text-[#475569]">{order.items.map((item) => `${item.product_name} × ${item.quantity}`).join(", ")}</p>
                  </div>
                  <div className="text-right">
                    <Badge className={order.payment_status === "completed" ? "bg-green-600" : order.payment_status === "rejected" ? "bg-red-600" : "bg-amber-500"} data-testid={`history-order-status-${order.order_id}`}>{order.payment_status}</Badge>
                    <p className="mt-2 font-bold text-[#0EA5E9]" data-testid={`history-order-total-${order.order_id}`}>₹{Number(order.total).toFixed(2)}</p>
                    {order.payment_status === "completed" && <Button variant="outline" className="mt-3" onClick={() => navigate(`/tickets/${order.order_id}`)} data-testid={`history-view-pass-${order.order_id}`}>View entry pass</Button>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TicketScanner = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [qrPayload, setQrPayload] = useState("");
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    if (!authLoading && !user?.is_admin) navigate("/");
  }, [authLoading, navigate, user]);

  const scanPass = async () => {
    if (!qrPayload.trim()) return toast.error("Scan or paste the QR pass first.");
    setScanning(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/tickets/scan`, { qr_payload: qrPayload.trim() }, { withCredentials: true });
      setResult({ valid: true, ...response.data });
      toast.success("Entry pass accepted.");
    } catch (error) {
      const message = error.response?.data?.detail || "Unable to scan this pass.";
      setResult({ valid: false, message });
      toast.error(message);
    } finally {
      setScanning(false);
    }
  };

  if (authLoading || !user?.is_admin) return <div className="min-h-screen flex items-center justify-center" data-testid="ticket-scan-loading">Loading scanner…</div>;

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="neumorphic-card rounded-3xl p-8" data-testid="ticket-scanner-page">
          <h1 className="text-4xl font-black text-[#0F172A]" style={{ fontFamily: "Nunito" }}>Entry Scanner</h1>
          <p className="mt-2 text-[#475569]">Scan with a hardware reader or paste the pass value. Each entry pass can be used once.</p>
          <Textarea value={qrPayload} onChange={(event) => setQrPayload(event.target.value)} className="mt-6 min-h-32" placeholder="Scanned Friendship Ad QR pass" data-testid="ticket-scan-payload-input" />
          <Button onClick={scanPass} disabled={scanning} className="mt-4 w-full bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="scan-ticket-button">{scanning ? "Checking pass…" : "Validate entry pass"}</Button>
          {result && (
            <div className={`mt-6 rounded-2xl p-5 ${result.valid ? "bg-green-50 border border-green-300" : "bg-red-50 border border-red-300"}`} data-testid="ticket-scan-result">
              <p className={`font-bold ${result.valid ? "text-green-800" : "text-red-800"}`}>{result.valid ? "Valid entry pass" : "Invalid entry pass"}</p>
              {result.valid ? <><p data-testid="scan-result-order-id">Order ID: {result.order_id}</p><p data-testid="scan-result-quantity">Tickets quantity: {result.ticket_quantity}</p><p data-testid="scan-result-amount">Amount before discount: ₹{Number(result.amount_without_discount).toFixed(2)}</p></> : <p>{result.message}</p>}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

const ParkPortal = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [activePark, setActivePark] = useState("");
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [parkName, setParkName] = useState("");
  const [counterId, setCounterId] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [cameraActive, setCameraActive] = useState(true);
  const [manualPayload, setManualPayload] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [redemptions, setRedemptions] = useState([]);
  const [summary, setSummary] = useState({ scans: 0, heads: 0, ticket_types: [] });
  const [filterYear, setFilterYear] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const scannerRef = useRef(null);
  const scanLockRef = useRef(false);
  const submitScanRef = useRef(null);

  const loadRedemptions = useCallback(async () => {
    try {
      const params = {};
      if (filterDate) params.date = filterDate;
      else {
        if (filterYear) params.year = filterYear;
        if (filterMonth) params.month = filterMonth;
      }
      const response = await axios.get(`${API}/park/redemptions`, { params, withCredentials: true });
      setRedemptions(response.data.records || []);
      setSummary(response.data.summary || { scans: 0, heads: 0, ticket_types: [] });
    } catch (error) {
      if (error.response?.status === 401) setAuthenticated(false);
      else toast.error(error.response?.data?.detail || "Unable to load park redemptions.");
    }
  }, [filterDate, filterMonth, filterYear]);

  useEffect(() => {
    const checkParkSession = async () => {
      try {
        const response = await axios.get(`${API}/park/auth/status`, { withCredentials: true });
        setAuthenticated(Boolean(response.data.authenticated));
        if (response.data.park_name) setActivePark(response.data.park_name);
      } finally {
        setCheckingAuth(false);
      }
    };
    checkParkSession();
  }, []);

  useEffect(() => {
    if (authenticated) loadRedemptions();
  }, [authenticated, loadRedemptions]);

  const submitScan = async (payload) => {
    if (!payload?.trim() || scanLockRef.current) return;
    scanLockRef.current = true;
    setScanning(true);
    setScanResult(null);
    setScanPreview(null);
    try {
      const trimmed = payload.trim();
      const response = await axios.post(`${API}/park/scan/preview`, { qr_payload: trimmed }, { withCredentials: true });
      setScanPreview({ ...response.data, payload: trimmed });
      setManualPayload("");
      setCameraActive(false);
      if (response.data.already_used) toast.error("This pass was already redeemed and is no longer valid.");
      else toast.success("Pass verified. Review details, then Redeem or Decline.");
    } catch (error) {
      const message = error.response?.data?.detail || "Unable to read this QR pass.";
      setScanResult({ valid: false, message });
      setCameraActive(false);
      toast.error(message);
    } finally {
      setScanning(false);
      scanLockRef.current = false;
    }
  };
  submitScanRef.current = submitScan;

  const confirmRedeem = async () => {
    if (!scanPreview?.payload) return;
    setActionBusy(true);
    try {
      const response = await axios.post(`${API}/park/scan/redeem`, { qr_payload: scanPreview.payload }, { withCredentials: true });
      setScanResult({ valid: true, ...response.data });
      setScanPreview(null);
      toast.success("Entry redeemed. This QR is now invalid.");
      await loadRedemptions();
    } catch (error) {
      const message = error.response?.data?.detail || "Unable to redeem this pass.";
      setScanResult({ valid: false, message });
      setScanPreview(null);
      toast.error(message);
    } finally {
      setActionBusy(false);
    }
  };

  const declineScan = async () => {
    const payload = scanPreview?.payload;
    setScanPreview(null);
    setScanResult(null);
    if (payload) { try { await axios.post(`${API}/park/scan/decline`, { qr_payload: payload }, { withCredentials: true }); } catch (_) {} }
    toast.message("Declined. This pass is still valid and can be scanned again.");
    setCameraActive(true);
  };

  useEffect(() => {
    if (!authenticated || !cameraActive) return undefined;
    const scanner = new Html5QrcodeScanner("park-qr-reader", { fps: 10, qrbox: { width: 250, height: 250 } }, false);
    scannerRef.current = scanner;
    scanner.render((decodedText) => submitScanRef.current?.(decodedText), () => {});
    return () => { scanner.clear().catch(() => {}); };
  }, [authenticated, cameraActive]);

  const login = async () => {
    if (!parkName.trim() || !password) return toast.error("Enter the park name and password.");
    setLoggingIn(true);
    try {
      const response = await axios.post(`${API}/park/auth/login`, { park_name: parkName.trim(), password, counter_id: counterId.trim() || null }, { withCredentials: true });
      setPassword("");
      setAuthenticated(true);
      setActivePark(response.data.park_name || parkName.trim().toUpperCase());
      toast.success("Park workspace unlocked.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to sign in to park workspace.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/park/auth/logout`, {}, { withCredentials: true });
    setAuthenticated(false);
    setRedemptions([]);
    setScanResult(null);
    setScanPreview(null);
  };

  const downloadStatement = async () => {
    try {
      const params = filterDate ? { date: filterDate } : { ...(filterYear ? { year: filterYear } : {}), ...(filterMonth ? { month: filterMonth } : {}) };
      const response = await axios.get(`${API}/park/redemptions/export`, { params, withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "hvp-redemption-statement.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Unable to download the park statement.");
    }
  };

  if (checkingAuth) return <div className="min-h-screen flex items-center justify-center" data-testid="park-auth-loading">Opening park workspace…</div>;
  if (!authenticated) return (
    <div className="min-h-screen py-12 px-4 flex items-center justify-center">
      <Card className="neumorphic-card rounded-3xl p-8 max-w-md w-full" data-testid="park-login-page">
        <h1 className="text-3xl font-black text-[#0F172A]" style={{ fontFamily: "Nunito" }}>Park Entry Workspace</h1>
        <p className="mt-3 text-[#475569]">Sign in to scan Friendship Ad entry passes and review park redemptions.</p>
        <Label htmlFor="park-name" className="block mt-6">Park name</Label>
        <Input id="park-name" value={parkName} onChange={(event) => setParkName(event.target.value)} autoComplete="username" className="mt-2" data-testid="park-name-input" />
        <Label htmlFor="park-counter" className="block mt-4">Counter / gate ID (optional)</Label>
        <Input id="park-counter" value={counterId} onChange={(event) => setCounterId(event.target.value.replace(/[^A-Za-z0-9_-]/g, ""))} placeholder="e.g. GATE-1" maxLength={40} className="mt-2" data-testid="park-counter-input" />
        <Label htmlFor="park-password" className="block mt-4">Park password</Label>
        <Input id="park-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} onKeyDown={(event) => event.key === "Enter" && login()} autoComplete="current-password" className="mt-2" data-testid="park-password-input" />
        <Button onClick={login} disabled={loggingIn} className="mt-6 w-full bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="park-login-button">{loggingIn ? "Signing in…" : "Open park workspace"}</Button>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen py-8 px-4" data-testid="park-workspace">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div><h1 className="text-4xl font-black text-[#0F172A]" style={{ fontFamily: "Nunito" }}>{activePark || "HVP"} Entry Desk</h1><p className="text-[#475569] mt-1">One scan per entry pass. Successful scans are recorded instantly.</p></div>
          <Button onClick={logout} variant="outline" data-testid="park-logout-button">End park session</Button>
        </div>
        <div className="grid gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <Card className="neumorphic-card rounded-3xl p-6" data-testid="park-scanner-panel">
            <h2 className="text-2xl font-bold text-[#0F172A]">Scan entry QR</h2>
            <p className="mt-1 text-sm text-[#475569]">Use the camera or paste a scanner value. A redeemed QR always shows an error.</p>
            {cameraActive ? <div id="park-qr-reader" className="mt-5 overflow-hidden rounded-2xl" data-testid="park-camera-scanner" /> : (!scanPreview && <Button onClick={() => { setScanResult(null); setScanPreview(null); setCameraActive(true); }} className="mt-5 bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="restart-park-camera-button">Scan next QR</Button>)}
            <div className="mt-5 border-t pt-5"><Label htmlFor="park-manual-qr">Scanner fallback</Label><Textarea id="park-manual-qr" value={manualPayload} onChange={(event) => setManualPayload(event.target.value)} className="mt-2 min-h-24" placeholder="Paste scanned QR pass" data-testid="park-manual-qr-input" /><Button onClick={() => submitScan(manualPayload)} disabled={scanning} variant="outline" className="mt-3" data-testid="park-manual-scan-button">{scanning ? "Checking…" : "Validate pass"}</Button></div>
            {scanPreview && <div className={`mt-5 rounded-2xl p-5 border ${scanPreview.already_used ? "bg-amber-50 border-amber-300" : "bg-sky-50 border-sky-300"}`} data-testid="park-scan-preview">
              <p className={`font-bold ${scanPreview.already_used ? "text-amber-800" : "text-sky-800"}`}>{scanPreview.already_used ? "Already redeemed — invalid" : "Ticket details"}</p>
              <div className="mt-3 text-sm space-y-1 text-[#0F172A]">
                <p><span className="text-[#475569]">Guest:</span> <strong data-testid="preview-guest-name">{scanPreview.customer_name}</strong></p>
                <p><span className="text-[#475569]">Email:</span> {scanPreview.customer_email || "—"}</p>
                <p><span className="text-[#475569]">Order:</span> {scanPreview.order_id}</p>
                <p><span className="text-[#475569]">Purchased:</span> {scanPreview.purchase_date ? new Date(scanPreview.purchase_date).toLocaleString() : "—"}</p>
                <div className="mt-2"><p className="text-[#475569]">Products:</p><ul className="mt-1 list-disc pl-5" data-testid="preview-items-list">{(scanPreview.items || []).map((it, idx) => <li key={idx}>{it.name} × {it.quantity}{it.type && it.type !== "ticket" ? ` (${it.type})` : ""}</li>)}</ul></div>
                <p className="mt-2"><span className="text-[#475569]">Total heads:</span> <strong data-testid="preview-heads">{scanPreview.ticket_quantity}</strong></p>
                {scanPreview.already_used && scanPreview.scanned_at && <p className="text-amber-800">Redeemed at {new Date(scanPreview.scanned_at).toLocaleString()}</p>}
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                {scanPreview.already_used ? (
                  <Button onClick={declineScan} disabled={actionBusy} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="park-scan-next-button">Scan next QR</Button>
                ) : (<>
                  <Button onClick={confirmRedeem} disabled={actionBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="park-redeem-button">{actionBusy ? "Redeeming…" : "Redeem"}</Button>
                  <Button onClick={declineScan} disabled={actionBusy} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="park-decline-button">Decline</Button>
                </>)}
              </div>
            </div>}
            {scanResult && <div className={`mt-5 rounded-2xl p-5 ${scanResult.valid ? "bg-emerald-50 border border-emerald-300" : "bg-red-50 border border-red-300"}`} data-testid="park-scan-result"><p className={`font-bold ${scanResult.valid ? "text-emerald-800" : "text-red-800"}`}>{scanResult.valid ? "Entry accepted" : "Entry rejected"}</p>{scanResult.valid ? <div className="mt-2 text-sm space-y-1"><p>Order: {scanResult.order_id}</p><p>Heads: {scanResult.heads}</p><p>Ticket type: {(scanResult.ticket_types || []).join(", ")}</p><p>Guest: {scanResult.person_name}</p></div> : <p className="mt-2 text-sm">{scanResult.message}</p>}</div>}
          </Card>
          <Card className="neumorphic-card rounded-3xl p-6" data-testid="park-redemption-chart">
            <div className="flex flex-wrap items-end justify-between gap-4"><div><h2 className="text-2xl font-bold text-[#0F172A]">Redemption chart</h2><p className="mt-1 text-sm text-[#475569]">All QR passes scanned through this park workspace.</p></div><Button onClick={downloadStatement} variant="outline" data-testid="download-park-statement-button">Download statement (CSV)</Button></div>
            <div className="mt-5 grid gap-3 sm:grid-cols-3"><div><Label htmlFor="redeem-date">Date</Label><Input id="redeem-date" type="date" value={filterDate} onChange={(event) => setFilterDate(event.target.value)} className="mt-2" data-testid="park-date-filter" /></div><div><Label htmlFor="redeem-year">Year</Label><Input id="redeem-year" type="number" min="2020" max="2100" value={filterYear} onChange={(event) => setFilterYear(event.target.value)} disabled={Boolean(filterDate)} className="mt-2" data-testid="park-year-filter" /></div><div><Label htmlFor="redeem-month">Month</Label><select id="redeem-month" value={filterMonth} onChange={(event) => setFilterMonth(event.target.value)} disabled={Boolean(filterDate)} className="mt-2 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="park-month-filter"><option value="">All months</option>{Array.from({ length: 12 }, (_, index) => <option key={index + 1} value={index + 1}>{new Date(2000, index, 1).toLocaleString("en", { month: "long" })}</option>)}</select></div></div>
            <Button onClick={loadRedemptions} className="mt-4 bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="apply-park-filters-button">Apply filters</Button>
            <div className="mt-5 grid grid-cols-2 gap-3"><div className="rounded-2xl bg-sky-50 p-4"><p className="text-xs text-[#475569]">QR scans</p><p className="text-2xl font-black text-[#0F172A]" data-testid="park-scan-count">{summary.scans}</p></div><div className="rounded-2xl bg-amber-50 p-4"><p className="text-xs text-[#475569]">Heads entered</p><p className="text-2xl font-black text-[#0F172A]" data-testid="park-heads-count">{summary.heads}</p></div></div>
            <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200"><table className="min-w-full text-sm" data-testid="park-redemption-table"><thead className="bg-slate-50 text-left text-[#475569]"><tr><th className="p-3">Date of Purchase</th><th className="p-3">Date of Redeem</th><th className="p-3">Order ID</th><th className="p-3">Type of Ticket</th><th className="p-3">No. of Heads</th><th className="p-3">Person Name</th><th className="p-3">Mail ID</th></tr></thead><tbody>{redemptions.length ? redemptions.map((record) => <tr key={record.redemption_id} className="border-t border-slate-100"><td className="p-3">{record.purchase_date ? new Date(record.purchase_date).toLocaleDateString() : "—"}</td><td className="p-3">{new Date(record.redeemed_at).toLocaleString()}</td><td className="p-3">{record.order_id}</td><td className="p-3">{(record.ticket_types || []).join(", ")}</td><td className="p-3">{record.heads}</td><td className="p-3">{record.person_name}</td><td className="p-3">{record.mail_id}</td></tr>) : <tr><td className="p-6 text-center text-[#475569]" colSpan="7">No QR passes have been redeemed for these filters.</td></tr>}</tbody></table></div>
          </Card>
        </div>
      </div>
    </div>
  );
};

const OperatorPortal = ({ expectedRole, title }) => {
  const { Razorpay } = useRazorpay();
  const [status, setStatus] = useState(null);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupBusy, setTopupBusy] = useState(false);
  const [autopayOn, setAutopayOn] = useState(false);
  const [autopayMethod, setAutopayMethod] = useState("wallet");
  const [agentsOverview, setAgentsOverview] = useState([]);
  const [adjustInputs, setAdjustInputs] = useState({});
  const [coupons, setCoupons] = useState([]);
  const [ads, setAds] = useState([]);
  const [parks, setParks] = useState([]);
  const [adDraft, setAdDraft] = useState({ business_name: "", email: "", place: "", destination_url: "", plan: "term5y" });
  const [operatorId, setOperatorId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [newAccount, setNewAccount] = useState({ operator_id: "", password: "", display_name: "", role: "admin", counter_id: "" });
  const [tier, setTier] = useState("gold");
  const [products, setProducts] = useState([]);
  const emptyProductDraft = { name: "", description: "", mrp: "", discount_percent: "20", image_url: "" };
  const [productDraft, setProductDraft] = useState(emptyProductDraft);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [statementFilters, setStatementFilters] = useState({ start: "", end: "", counter_id: "", agent_id: "", payment_status: "", park_name: "" });
  const [publishingDraft, setPublishingDraft] = useState({ parkName: "", parkPassword: "", parkLocation: "", parkDescription: "", couponCode: "", couponDiscount: "10", couponUntil: "", sponsorTitle: "", sponsorImage: "", sponsorUrl: "", reelPrompt: "Families enjoying the wave pool together" });
  const [generatingReel, setGeneratingReel] = useState(false);
  const emptyEposDraft = { customer_name: "", customer_email: "", mode: "paid", note: "", items: [{ name: "", quantity: "1", price: "" }] };
  const [eposDraft, setEposDraft] = useState(emptyEposDraft);
  const [eposResult, setEposResult] = useState(null);
  const [eposBusy, setEposBusy] = useState(false);

  const checkStatus = useCallback(async () => {
    try { const response = await axios.get(`${API}/operator/auth/status`, { withCredentials: true }); setStatus(response.data.authenticated ? response.data : null); return response.data; }
    catch (_) { setStatus(null); return { authenticated: false }; }
    finally { setLoading(false); }
  }, []);
  const loadAccounts = useCallback(async () => {
    try { const response = await axios.get(`${API}/operator/accounts`, { withCredentials: true }); setAccounts(response.data); } catch (_) {}
  }, []);
  const loadAgentsOverview = useCallback(async () => {
    try { const response = await axios.get(`${API}/operator/agents/overview`, { withCredentials: true }); setAgentsOverview(response.data); } catch (_) {}
  }, []);
  const loadCoupons = useCallback(async () => {
    try { const response = await axios.get(`${API}/operator/coupons`, { withCredentials: true }); setCoupons(response.data); } catch (_) {}
  }, []);
  const loadAds = useCallback(async () => {
    try { const response = await axios.get(`${API}/operator/ads`, { withCredentials: true }); setAds(response.data); } catch (_) {}
  }, []);
  const loadParks = useCallback(async () => {
    try { const response = await axios.get(`${API}/operator/parks`, { withCredentials: true }); setParks(response.data); } catch (_) {}
  }, []);
  useEffect(() => { checkStatus(); }, [checkStatus]);
  useEffect(() => { if (status && status.role === "agent") { setAutopayOn(Boolean(status.autopay_enabled)); setAutopayMethod(status.autopay_method || "wallet"); if (status.membership_tier) setTier(status.membership_tier); } }, [status]);
  useEffect(() => { if (status && ["owner", "chief_admin"].includes(status.role)) loadAccounts(); }, [status, loadAccounts]);
  useEffect(() => { if (status && ["owner", "chief_admin", "admin"].includes(status.role)) loadAgentsOverview(); }, [status, loadAgentsOverview]);
  useEffect(() => { if (status && ["owner", "chief_admin", "admin"].includes(status.role)) { loadCoupons(); loadAds(); loadParks(); } }, [status, loadCoupons, loadAds, loadParks]);
  const loadProducts = useCallback(async () => { try { const response = await axios.get(`${API}/operator/products`, { withCredentials: true }); setProducts(response.data); } catch (_) {} }, []);
  useEffect(() => { if (status && status.role !== "agent") loadProducts(); }, [status, loadProducts]);

  const login = async () => {
    try {
      const response = await axios.post(`${API}/operator/auth/login`, { operator_id: operatorId, password }, { withCredentials: true });
      if (expectedRole && response.data.role !== expectedRole) { await axios.post(`${API}/operator/auth/logout`, {}, { withCredentials: true }); throw new Error("Use the workspace that matches your account role."); }
      setPassword(""); setStatus(response.data); await checkStatus(); toast.success("Workspace unlocked.");
    } catch (error) { toast.error(error.response?.data?.detail || error.message || "Unable to sign in."); }
  };
  const addAccount = async () => {
    try { await axios.post(`${API}/operator/accounts`, { ...newAccount, counter_id: newAccount.counter_id.trim() || null }, { withCredentials: true }); setNewAccount({ operator_id: "", password: "", display_name: "", role: "admin", counter_id: "" }); await loadAccounts(); toast.success("Account added."); } catch (error) { toast.error(error.response?.data?.detail || "Unable to add account."); }
  };
  const deleteAccount = async (account) => {
    if (!window.confirm(`Remove ${account.operator_id}?`)) return;
    try { await axios.delete(`${API}/operator/accounts/${encodeURIComponent(account.operator_id)}`, { withCredentials: true }); await loadAccounts(); toast.success("Account removed."); } catch (error) { toast.error(error.response?.data?.detail || "Unable to remove account."); }
  };
  const apiErrorMessage = (error, fallback) => { const detail = error.response?.data?.detail; return typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((item) => item.msg).join("; ") : fallback; };
  const adjustAgentWallet = async (operatorId, sign) => {
    const raw = Number(adjustInputs[operatorId]);
    if (!(raw > 0)) return toast.error("Enter an amount greater than 0.");
    try {
      const res = await axios.post(`${API}/operator/agents/${encodeURIComponent(operatorId)}/wallet-adjust`, { amount: sign * raw }, { withCredentials: true });
      toast.success(res.data.message);
      setAdjustInputs((prev) => ({ ...prev, [operatorId]: "" }));
      await loadAgentsOverview();
    } catch (error) { toast.error(apiErrorMessage(error, "Unable to adjust wallet.")); }
  };
  const uploadProductImage = async (file) => {
    const form = new FormData(); form.append("file", file);
    const response = await axios.post(`${API}/operator/products/upload-image`, form, { withCredentials: true });
    return response.data.image_url;
  };
  const handleDraftImage = async (event) => {
    const file = event.target.files?.[0]; event.target.value = "";
    if (!file) return;
    setUploadingImage(true);
    try { const imageUrl = await uploadProductImage(file); setProductDraft((draft) => ({ ...draft, image_url: imageUrl })); toast.success("Image uploaded."); }
    catch (error) { toast.error(apiErrorMessage(error, "Unable to upload image.")); }
    finally { setUploadingImage(false); }
  };
  const addProduct = async () => {
    const mrp = Number(productDraft.mrp); const discount = Number(productDraft.discount_percent);
    if (!productDraft.name.trim() || !(mrp > 0)) return toast.error("Enter a product name and an MRP greater than 0.");
    if (!(discount >= 0 && discount <= 100)) return toast.error("Discount must be between 0 and 100.");
    try { await axios.post(`${API}/operator/products`, { name: productDraft.name.trim(), description: productDraft.description.trim() || productDraft.name.trim(), mrp, discount_percent: discount, category: "ticket", region: "West Bengal", sub_category: "Barasat Bira", stock: 1000, image_url: productDraft.image_url.trim() || null }, { withCredentials: true }); setProductDraft(emptyProductDraft); await loadProducts(); toast.success("Product added."); } catch (error) { toast.error(apiErrorMessage(error, "Unable to add product.")); }
  };
  const editProduct = async (product) => { const mrp = window.prompt("MRP", product.mrp); const discount = window.prompt("Discount percent", product.discount_percent); if (mrp === null || discount === null) return; try { await axios.put(`${API}/operator/products/${product.product_id}`, { mrp: Number(mrp), discount_percent: Number(discount) }, { withCredentials: true }); await loadProducts(); toast.success("Product updated."); } catch (error) { toast.error(apiErrorMessage(error, "Unable to update product.")); } };
  const replaceProductImage = async (product, event) => {
    const file = event.target.files?.[0]; event.target.value = "";
    if (!file) return;
    try { const imageUrl = await uploadProductImage(file); await axios.put(`${API}/operator/products/${product.product_id}`, { image_url: imageUrl }, { withCredentials: true }); await loadProducts(); toast.success("Product image updated."); }
    catch (error) { toast.error(apiErrorMessage(error, "Unable to update product image.")); }
  };
  const downloadStatement = async (kind) => {
    const { start, end, counter_id, agent_id, payment_status, park_name } = statementFilters;
    if (!start || !end) return toast.error("Choose a start and end date.");
    if (end < start) return toast.error("End date must be on or after the start date.");
    // End date is inclusive in the UI; the API treats it as an exclusive upper bound.
    const endExclusive = new Date(`${end}T00:00:00Z`); endExclusive.setUTCDate(endExclusive.getUTCDate() + 1);
    const params = { start: `${start}T00:00:00+00:00`, end: `${endExclusive.toISOString().slice(0, 10)}T00:00:00+00:00` };
    if (counter_id.trim()) params.counter_id = counter_id.trim();
    if (agent_id.trim() && status.role !== "agent") params.agent_id = agent_id.trim();
    if (kind === "sales" && payment_status) params.payment_status = payment_status;
    if (kind === "redemptions" && park_name) params.park_name = park_name;
    try { await downloadCsv(`/operator/statements/${kind}`, params, `${kind}-statement-${start}-to-${end}.csv`); }
    catch (error) { toast.error(`Unable to download the ${kind} statement.`); }
  };
  const removeProduct = async (product) => { if (!window.confirm(`Remove ${product.name}?`)) return; try { await axios.delete(`${API}/operator/products/${product.product_id}`, { withCredentials: true }); await loadProducts(); toast.success("Product removed."); } catch (error) { toast.error("Unable to remove product."); } };
  const chooseMembership = async () => {
    try { const response = await axios.post(`${API}/agent/membership`, { tier }, { withCredentials: true }); toast.success(response.data.message); await checkStatus(); } catch (error) { toast.error(error.response?.data?.detail || "Membership could not be activated."); }
  };
  const topUpWallet = async () => {
    const amt = Math.round((Number(topupAmount) || 0) * 100) / 100;
    if (!(amt >= 1)) return toast.error("Enter an amount of at least ₹1.");
    if (!Razorpay) return toast.error("Payment library is still loading. Try again in a moment.");
    setTopupBusy(true);
    try {
      const created = await axios.post(`${API}/agent/wallet/razorpay/create`, { amount: amt }, { withCredentials: true });
      new Razorpay({
        key: created.data.key_id,
        amount: created.data.amount,
        currency: "INR",
        name: "Friendship Ad",
        description: `Wallet top-up ₹${amt}`,
        order_id: created.data.order_id,
        handler: async (payment) => {
          try { const res = await axios.post(`${API}/agent/wallet/razorpay/verify`, payment, { withCredentials: true }); toast.success(res.data.message); setTopupAmount(""); await checkStatus(); }
          catch (err) { toast.error(err.response?.data?.detail || "Wallet top-up could not be verified."); }
        },
      }).open();
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to start wallet top-up."); }
    finally { setTopupBusy(false); }
  };
  const saveAutopay = async () => {
    try { const res = await axios.post(`${API}/agent/autopay`, { enabled: autopayOn, method: autopayMethod, tier }, { withCredentials: true }); toast.success(res.data.message); await checkStatus(); }
    catch (error) { toast.error(error.response?.data?.detail || "Unable to save autopay settings."); }
  };
  const renewViaRazorpay = async () => {
    if (!Razorpay) return toast.error("Payment library is still loading. Try again in a moment.");
    setTopupBusy(true);
    try {
      const created = await axios.post(`${API}/agent/membership/razorpay/create`, {}, { withCredentials: true });
      new Razorpay({
        key: created.data.key_id,
        amount: created.data.amount,
        currency: "INR",
        name: "Friendship Ad",
        description: "Monthly membership renewal",
        order_id: created.data.order_id,
        handler: async (payment) => {
          try { const res = await axios.post(`${API}/agent/membership/razorpay/verify`, payment, { withCredentials: true }); toast.success(res.data.message); await checkStatus(); }
          catch (err) { toast.error(err.response?.data?.detail || "Renewal could not be verified."); }
        },
      }).open();
    } catch (error) { toast.error(error.response?.data?.detail || "Unable to start membership renewal."); }
    finally { setTopupBusy(false); }
  };
  const publishPark = async () => { if (!publishingDraft.parkName.trim() || !publishingDraft.parkPassword) return toast.error("Enter a Park ID and password."); try { await axios.post(`${API}/operator/parks`, { park_id: publishingDraft.parkName, password: publishingDraft.parkPassword }, { withCredentials: true }); toast.success("Park login created."); setPublishingDraft((d) => ({ ...d, parkName: "", parkPassword: "" })); await loadParks(); } catch (error) { toast.error(apiErrorMessage(error, "Unable to create park login.")); } };
  const deletePark = async (parkId) => { if (!window.confirm(`Remove park login ${parkId}?`)) return; try { await axios.delete(`${API}/operator/parks/${encodeURIComponent(parkId)}`, { withCredentials: true }); toast.success("Park login removed."); setStatementFilters((f) => f.park_name === parkId ? { ...f, park_name: "" } : f); await loadParks(); } catch (error) { toast.error(apiErrorMessage(error, "Unable to remove park login.")); } };
  const publishCoupon = async () => { if (!publishingDraft.couponCode.trim()) return toast.error("Enter a coupon code."); try { await axios.post(`${API}/operator/coupons`, { code: publishingDraft.couponCode, discount_percent: Number(publishingDraft.couponDiscount), valid_until: publishingDraft.couponUntil }, { withCredentials: true }); toast.success("Coupon added."); setPublishingDraft((d) => ({ ...d, couponCode: "" })); await loadCoupons(); } catch (error) { toast.error(apiErrorMessage(error, "Unable to add coupon.")); } };
  const deleteCoupon = async (couponId) => { if (!window.confirm("Remove this coupon?")) return; try { await axios.delete(`${API}/operator/coupons/${couponId}`, { withCredentials: true }); toast.success("Coupon removed."); await loadCoupons(); } catch (error) { toast.error(apiErrorMessage(error, "Unable to remove coupon.")); } };
  const createAd = async () => { if (!adDraft.business_name.trim() || !adDraft.email.trim()) return toast.error("Enter advertiser name and email."); try { const res = await axios.post(`${API}/operator/ads`, adDraft, { withCredentials: true }); toast.success("Advertisement created."); setAdDraft({ business_name: "", email: "", place: "", destination_url: "", plan: "term5y" }); await loadAds(); window.open(`${BACKEND_URL}${res.data.invoice_url}`, "_blank"); } catch (error) { toast.error(apiErrorMessage(error, "Unable to create advertisement.")); } };
  const stopAd = async (adId) => { if (!window.confirm("Stop this advertisement?")) return; try { await axios.post(`${API}/operator/ads/${adId}/stop`, {}, { withCredentials: true }); toast.success("Advertisement stopped."); await loadAds(); } catch (error) { toast.error(apiErrorMessage(error, "Unable to stop advertisement.")); } };
  const publishPark_unused = async () => {};
  const publishSponsor = async () => { try { await axios.post(`${API}/operator/sponsor-banners`, { title: publishingDraft.sponsorTitle, image_url: publishingDraft.sponsorImage, destination_url: publishingDraft.sponsorUrl || null }, { withCredentials: true }); toast.success("Sponsor banner published."); } catch (error) { toast.error(apiErrorMessage(error, "Unable to publish sponsor banner.")); } };
  const generateReel = async () => { setGeneratingReel(true); try { await axios.post(`${API}/operator/reels/generate`, { prompt: publishingDraft.reelPrompt }, { withCredentials: true }); toast.success("New park reel frame published."); } catch (error) { toast.error(apiErrorMessage(error, "Unable to generate park image.")); } finally { setGeneratingReel(false); } };
  const logout = async () => { await axios.post(`${API}/operator/auth/logout`, {}, { withCredentials: true }); setStatus(null); };
  const eposTotal = eposDraft.items.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
  const updateEposItem = (index, field, value) => setEposDraft((d) => ({ ...d, items: d.items.map((it, i) => i === index ? { ...it, [field]: value } : it) }));
  const addEposItem = () => setEposDraft((d) => ({ ...d, items: [...d.items, { name: "", quantity: "1", price: "" }] }));
  const removeEposItem = (index) => setEposDraft((d) => ({ ...d, items: d.items.length > 1 ? d.items.filter((_, i) => i !== index) : d.items }));
  const generateEpos = async () => {
    if (!eposDraft.customer_name.trim()) return toast.error("Enter the customer / guest name.");
    const items = eposDraft.items.map((it) => ({ name: it.name.trim(), quantity: Number(it.quantity), price: Number(it.price) || 0 })).filter((it) => it.name && it.quantity > 0);
    if (!items.length) return toast.error("Add at least one ticket line with a name and quantity.");
    setEposBusy(true);
    try {
      const body = { customer_name: eposDraft.customer_name.trim(), items, mode: eposDraft.mode, note: eposDraft.note.trim() || null };
      if (eposDraft.customer_email.trim()) body.customer_email = eposDraft.customer_email.trim();
      const response = await axios.post(`${API}/epos/generate-ticket`, body, { withCredentials: true });
      setEposResult(response.data);
      toast.success(`${eposDraft.mode === "complimentary" ? "Complimentary" : "Paid"} ticket generated.`);
      setEposDraft(emptyEposDraft);
    } catch (error) { toast.error(apiErrorMessage(error, "Unable to generate the EPOS ticket.")); }
    finally { setEposBusy(false); }
  };
  const printEposTicket = () => {
    if (!eposResult) return;
    const r = eposResult;
    const rows = (r.items || []).map((it) => `<tr><td>${it.product_name}</td><td style="text-align:center">${it.quantity}</td><td style="text-align:right">₹${Number(it.price).toFixed(2)}</td><td style="text-align:right">₹${Number(it.total).toFixed(2)}</td></tr>`).join("");
    const win = window.open("", "_blank", "width=420,height=720");
    if (!win) return toast.error("Allow pop-ups to print the ticket.");
    win.document.write(`<html><head><title>Friendship Ad Ticket ${r.order_id}</title><style>body{font-family:Arial,sans-serif;padding:18px;color:#0F172A}h1{font-size:20px;margin:0}table{width:100%;border-collapse:collapse;margin-top:10px;font-size:13px}td,th{border-bottom:1px solid #e2e8f0;padding:6px}img{width:220px;height:220px;display:block;margin:14px auto}</style></head><body><h1>Friendship Ad</h1><p style="margin:2px 0;color:#475569">Happy Valley Park · ${r.mode === "complimentary" ? "COMPLIMENTARY" : "PAID"} INVOICE</p><p style="margin:8px 0 0"><strong>${r.customer_name}</strong><br/>${r.customer_email || ""}</p><p style="margin:4px 0;color:#475569">Order ${r.order_id} · ${new Date(r.created_at).toLocaleString()}</p><table><thead><tr><th style="text-align:left">Item</th><th>Qty</th><th style="text-align:right">Price</th><th style="text-align:right">Total</th></tr></thead><tbody>${rows}</tbody></table><p style="text-align:right;margin-top:8px;font-weight:bold">Total: ₹${Number(r.total).toFixed(2)}</p>${r.note ? `<p style="color:#475569">Note: ${r.note}</p>` : ""}<img src="data:image/png;base64,${r.qr_code}" alt="Entry QR"/><p style="text-align:center;color:#475569;font-size:12px">Scan at the park gate · ${r.ticket_quantity} head(s)</p></body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };
  const eposCard = (
    <Card className="neumorphic-card rounded-3xl mt-6 p-7" data-testid="epos-panel">
      <h2 className="text-2xl font-bold">EPOS — Issue QR Ticket</h2>
      <p className="mt-1 text-sm text-[#475569]">Generate a custom invoice QR ticket (paid or complimentary). Valid at the park scanner.</p>
      <div className="grid gap-3 md:grid-cols-2 mt-4">
        <Input placeholder="Guest / customer name" value={eposDraft.customer_name} onChange={(e) => setEposDraft({ ...eposDraft, customer_name: e.target.value })} data-testid="epos-customer-name" />
        <Input placeholder="Guest email (optional)" value={eposDraft.customer_email} onChange={(e) => setEposDraft({ ...eposDraft, customer_email: e.target.value })} data-testid="epos-customer-email" />
      </div>
      <div className="mt-4 space-y-3">
        {eposDraft.items.map((it, index) => (
          <div key={index} className="grid gap-2 md:grid-cols-[1fr_90px_110px_40px] items-center">
            <Input placeholder="Ticket / product name" value={it.name} onChange={(e) => updateEposItem(index, "name", e.target.value)} data-testid={`epos-item-name-${index}`} />
            <Input type="number" min="1" placeholder="Qty" value={it.quantity} onChange={(e) => updateEposItem(index, "quantity", e.target.value)} data-testid={`epos-item-qty-${index}`} />
            <Input type="number" min="0" step="0.01" placeholder="Price ₹" value={it.price} onChange={(e) => updateEposItem(index, "price", e.target.value)} data-testid={`epos-item-price-${index}`} />
            <Button variant="outline" onClick={() => removeEposItem(index)} data-testid={`epos-item-remove-${index}`}>✕</Button>
          </div>
        ))}
        <Button variant="outline" onClick={addEposItem} data-testid="epos-add-item">+ Add line item</Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2 mt-4">
        <select value={eposDraft.mode} onChange={(e) => setEposDraft({ ...eposDraft, mode: e.target.value })} className="border rounded-md px-3 h-10" data-testid="epos-mode-select">
          <option value="paid">Paid (collect ₹{eposTotal.toFixed(2)})</option>
          <option value="complimentary">Complimentary (free)</option>
        </select>
        <Input placeholder="Invoice note (optional)" value={eposDraft.note} onChange={(e) => setEposDraft({ ...eposDraft, note: e.target.value })} data-testid="epos-note" />
      </div>
      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm text-[#475569]">Payable: <strong className="text-[#0F172A]">₹{(eposDraft.mode === "complimentary" ? 0 : eposTotal).toFixed(2)}</strong></p>
        <Button onClick={generateEpos} disabled={eposBusy} className="bg-[#0F766E] hover:bg-[#0d655e]" data-testid="epos-generate-button">{eposBusy ? "Generating…" : "Generate QR ticket"}</Button>
      </div>
      {eposResult && (
        <div className="mt-6 rounded-2xl border border-emerald-300 bg-emerald-50 p-5 text-center" data-testid="epos-result">
          <p className="font-bold text-emerald-800">Ticket ready · {eposResult.mode === "complimentary" ? "Complimentary" : `₹${Number(eposResult.total).toFixed(2)} paid`}</p>
          <p className="text-sm text-[#475569] mt-1">{eposResult.customer_name} · Order {eposResult.order_id} · {eposResult.ticket_quantity} head(s)</p>
          <img src={`data:image/png;base64,${eposResult.qr_code}`} alt="Entry QR" className="mx-auto mt-3 w-52 h-52" data-testid="epos-qr-image" />
          <div className="mt-3 flex justify-center gap-3">
            <Button onClick={printEposTicket} className="bg-[#0EA5E9]" data-testid="epos-print-button">Print ticket</Button>
            <Button variant="outline" onClick={() => setEposResult(null)} data-testid="epos-clear-button">New ticket</Button>
          </div>
        </div>
      )}
    </Card>
  );
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading workspace…</div>;
  if (!status) return <div className="min-h-screen flex items-center justify-center px-4"><Card className="neumorphic-card rounded-3xl p-8 max-w-md w-full" data-testid="operator-login-page"><h1 className="text-3xl font-black text-[#0F172A]">{title}</h1><Input className="mt-6" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} placeholder="Workspace ID" data-testid="operator-id-input" /><Input className="mt-3" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} placeholder="Password" data-testid="operator-password-input" /><Button onClick={login} className="mt-5 w-full bg-[#0EA5E9]" data-testid="operator-login-button">Open workspace</Button></Card></div>;
  if (expectedRole && status.role !== expectedRole) return <div className="min-h-screen flex items-center justify-center"><Card className="p-8 text-center"><p>This account belongs to the {status.role} workspace.</p><Button onClick={logout} className="mt-4">Sign out</Button></Card></div>;
  const canManage = ["owner", "chief_admin"].includes(status.role);
  const productCard = (
    <Card className="neumorphic-card rounded-3xl mt-6 p-7">
      <h2 className="text-2xl font-bold">Product Operations</h2>
      <div className="grid gap-3 md:grid-cols-4 mt-4">
        <Input placeholder="Product name" value={productDraft.name} onChange={(e)=>setProductDraft({...productDraft,name:e.target.value})} data-testid="operator-product-name"/>
        <Input placeholder="MRP" type="number" min="0.01" step="0.01" value={productDraft.mrp} onChange={(e)=>setProductDraft({...productDraft,mrp:e.target.value})} data-testid="operator-product-mrp"/>
        <Input placeholder="Discount %" type="number" min="0" max="100" step="0.01" value={productDraft.discount_percent} onChange={(e)=>setProductDraft({...productDraft,discount_percent:e.target.value})} data-testid="operator-product-discount"/>
        <Input placeholder="Description" value={productDraft.description} onChange={(e)=>setProductDraft({...productDraft,description:e.target.value})} data-testid="operator-product-description"/>
        <Input className="md:col-span-2" placeholder="Image URL (or upload a file)" value={productDraft.image_url} onChange={(e)=>setProductDraft({...productDraft,image_url:e.target.value})} data-testid="operator-product-image-url"/>
        <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleDraftImage} disabled={uploadingImage} data-testid="operator-product-image-file"/>
        <Button onClick={addProduct} disabled={uploadingImage} data-testid="operator-add-product">{uploadingImage ? "Uploading…" : "Add product"}</Button>
      </div>
      {productDraft.image_url && <img src={resolveImageUrl(productDraft.image_url)} alt="Product preview" className="mt-3 w-24 h-24 rounded-lg object-cover" data-testid="operator-product-image-preview"/>}
      <div className="mt-5 space-y-2">
        {products.map((product)=>(
          <div key={product.product_id} className="flex justify-between items-center gap-3">
            <span className="flex items-center gap-3">{product.image_url && <img src={resolveImageUrl(product.image_url)} alt="" className="w-10 h-10 rounded object-cover"/>}{product.name} — MRP ₹{product.mrp} · ₹{product.final_price}</span>
            <span className="flex gap-2">
              <label className="inline-flex items-center px-3 border rounded-md text-sm cursor-pointer" data-testid={`operator-image-product-${product.product_id}`}>Image<input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e)=>replaceProductImage(product, e)}/></label>
              <Button variant="outline" onClick={()=>editProduct(product)} data-testid={`operator-edit-product-${product.product_id}`}>Edit</Button>
              <Button variant="outline" onClick={()=>removeProduct(product)} data-testid={`operator-remove-product-${product.product_id}`}>Remove</Button>
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
  const canOversee = ["owner", "chief_admin", "admin"].includes(status.role);
  const agentsOverviewCard = (
    <Card className="neumorphic-card rounded-3xl mt-6 p-7" data-testid="agents-overview-panel">
      <h2 className="text-2xl font-bold">Agents — Wallet &amp; Sales</h2>
      <p className="mt-1 text-sm text-[#475569]">Live view of every agent's wallet, top-ups, membership and sales. Add or deduct wallet balance below.</p>
      <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200">
        <table className="min-w-full text-sm" data-testid="agents-overview-table">
          <thead className="bg-slate-50 text-left text-[#475569]"><tr><th className="p-3">Agent</th><th className="p-3">Wallet</th><th className="p-3">Membership</th><th className="p-3">Autopay</th><th className="p-3">Top-ups</th><th className="p-3">Sales</th><th className="p-3">Adjust wallet</th></tr></thead>
          <tbody>
            {agentsOverview.length ? agentsOverview.map((a) => (
              <tr key={a.operator_id} className="border-t border-slate-100" data-testid={`agent-row-${a.operator_id}`}>
                <td className="p-3"><div className="font-semibold text-[#0F172A]">{a.display_name || a.operator_id}</div><div className="text-xs text-[#475569]">{a.operator_id}</div></td>
                <td className="p-3 font-bold text-[#0F172A]" data-testid={`agent-wallet-${a.operator_id}`}>₹{Number(a.wallet_balance).toFixed(2)}</td>
                <td className="p-3">{a.membership_tier ? `${a.membership_tier} · ${a.membership_status || ""}` : "—"}{a.renewal_due && <span className="ml-1 text-red-600 font-semibold">(due)</span>}</td>
                <td className="p-3">{a.autopay_enabled ? a.autopay_method : "off"}</td>
                <td className="p-3">₹{Number(a.total_topups).toFixed(2)} <span className="text-xs text-[#475569]">({a.topup_count})</span></td>
                <td className="p-3">{a.sales_count} <span className="text-xs text-[#475569]">· MRP ₹{Number(a.sales_invoiced).toFixed(2)}</span></td>
                <td className="p-3"><div className="flex items-center gap-2"><Input type="number" min="0" step="0.01" placeholder="₹" value={adjustInputs[a.operator_id] || ""} onChange={(e) => setAdjustInputs((prev) => ({ ...prev, [a.operator_id]: e.target.value }))} className="max-w-[90px] h-9" data-testid={`agent-adjust-input-${a.operator_id}`} /><Button onClick={() => adjustAgentWallet(a.operator_id, 1)} className="h-9 bg-emerald-600 hover:bg-emerald-700" data-testid={`agent-add-${a.operator_id}`}>Add</Button><Button onClick={() => adjustAgentWallet(a.operator_id, -1)} variant="outline" className="h-9 border-red-300 text-red-700" data-testid={`agent-deduct-${a.operator_id}`}>Deduct</Button></div></td>
              </tr>
            )) : <tr><td className="p-6 text-center text-[#475569]" colSpan="7">No agents yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
  const managementCard = (
    <Card className="neumorphic-card rounded-3xl mt-6 p-7" data-testid="management-panel">
      <h2 className="text-2xl font-bold">Parks, Coupons &amp; Advertisements</h2>
      {canManage && <div className="mt-5" data-testid="manage-parks">
        <h3 className="text-lg font-bold">Park scanner logins</h3>
        <p className="text-xs text-[#475569]">Create a Park ID + password that can sign in to the Park scanner portal.</p>
        <div className="mt-2 grid gap-2 md:grid-cols-3">
          <Input placeholder="Park ID" value={publishingDraft.parkName} onChange={(e) => setPublishingDraft({ ...publishingDraft, parkName: e.target.value.toUpperCase() })} data-testid="park-id-input" />
          <Input type="password" placeholder="Password" value={publishingDraft.parkPassword} onChange={(e) => setPublishingDraft({ ...publishingDraft, parkPassword: e.target.value })} data-testid="park-password-input" />
          <Button onClick={publishPark} className="bg-[#0EA5E9]" data-testid="add-park-button">Add park login</Button>
        </div>
        <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200"><table className="min-w-full text-sm" data-testid="parks-table"><thead className="bg-slate-50 text-left text-[#475569]"><tr><th className="p-3">Park ID</th><th className="p-3">Type</th><th className="p-3">Action</th></tr></thead><tbody>{parks.length ? parks.map((p) => <tr key={p.park_id} className="border-t border-slate-100" data-testid={`park-row-${p.park_id}`}><td className="p-3 font-semibold">{p.park_id}</td><td className="p-3">{p.default ? "Default" : "Added"}</td><td className="p-3">{p.default ? <span className="text-xs text-[#475569]">built-in</span> : <Button variant="outline" className="h-8 border-red-300 text-red-700" onClick={() => deletePark(p.park_id)} data-testid={`park-remove-${p.park_id}`}>Remove</Button>}</td></tr>) : <tr><td className="p-4 text-[#475569]" colSpan="3">No park logins.</td></tr>}</tbody></table></div>
      </div>}
      {canManage && <div className="mt-6 border-t pt-5" data-testid="manage-coupons">
        <h3 className="text-lg font-bold">Coupons</h3>
        <div className="mt-2 grid gap-2 md:grid-cols-4">
          <Input placeholder="CODE" value={publishingDraft.couponCode} onChange={(e) => setPublishingDraft({ ...publishingDraft, couponCode: e.target.value.toUpperCase() })} data-testid="coupon-code-input" />
          <Input type="number" placeholder="Discount %" value={publishingDraft.couponDiscount} onChange={(e) => setPublishingDraft({ ...publishingDraft, couponDiscount: e.target.value })} data-testid="coupon-discount-input" />
          <Input type="date" value={publishingDraft.couponUntil} onChange={(e) => setPublishingDraft({ ...publishingDraft, couponUntil: e.target.value })} data-testid="coupon-until-input" />
          <Button onClick={publishCoupon} className="bg-[#0EA5E9]" data-testid="add-coupon-button">Add coupon</Button>
        </div>
        <div className="mt-3 space-y-2" data-testid="coupon-list">{coupons.length ? coupons.map((c) => <div key={c.coupon_id} className="flex items-center justify-between text-sm" data-testid={`coupon-row-${c.code}`}><span>{c.code} — {c.discount_percent}% · till {new Date(c.valid_until).toLocaleDateString()}</span><Button variant="outline" className="h-8 border-red-300 text-red-700" onClick={() => deleteCoupon(c.coupon_id)} data-testid={`coupon-remove-${c.code}`}>Remove</Button></div>) : <p className="text-sm text-[#475569]">No coupons yet.</p>}</div>
      </div>}
      <div className="mt-6 border-t pt-5" data-testid="manage-ads">
        <h3 className="text-lg font-bold">Advertisements &amp; Sponsorships</h3>
        <p className="text-xs text-[#475569]">Create an ad directly, or review who is advertising and the term left.</p>
        <div className="mt-2 grid gap-2 md:grid-cols-5">
          <Input placeholder="Advertiser name" value={adDraft.business_name} onChange={(e) => setAdDraft({ ...adDraft, business_name: e.target.value })} data-testid="ad-name-input" />
          <Input placeholder="Email (invoice)" value={adDraft.email} onChange={(e) => setAdDraft({ ...adDraft, email: e.target.value })} data-testid="ad-email-input" />
          <Input placeholder="Place" value={adDraft.place} onChange={(e) => setAdDraft({ ...adDraft, place: e.target.value })} data-testid="ad-place-input" />
          <select value={adDraft.plan} onChange={(e) => setAdDraft({ ...adDraft, plan: e.target.value })} className="border rounded-md px-3 h-10" data-testid="ad-plan-select"><option value="term5y">5-year (₹4,50,000)</option><option value="monthly">Monthly (₹10,000)</option></select>
          <Button onClick={createAd} className="bg-[#0F766E]" data-testid="create-ad-button">Create ad</Button>
        </div>
        <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
          <table className="min-w-full text-sm" data-testid="ads-table">
            <thead className="bg-slate-50 text-left text-[#475569]"><tr><th className="p-3">Advertiser</th><th className="p-3">Plan</th><th className="p-3">Status</th><th className="p-3">Term left</th><th className="p-3">Source</th><th className="p-3">Action</th></tr></thead>
            <tbody>{ads.length ? ads.map((ad) => <tr key={ad.ad_id} className="border-t border-slate-100" data-testid={`ad-row-${ad.ad_id}`}>
              <td className="p-3"><div className="font-semibold">{ad.business_name}</div><div className="text-xs text-[#475569]">{ad.email}{ad.place ? ` · ${ad.place}` : ""}</div></td>
              <td className="p-3">{ad.plan === "monthly" ? "₹10k/mo" : "₹4.5L/5y"}</td>
              <td className="p-3">{ad.status}</td>
              <td className="p-3">{ad.term?.days_left != null ? `${ad.term.days_left} days` : "—"}</td>
              <td className="p-3">{ad.source}</td>
              <td className="p-3 flex gap-2"><a href={`${BACKEND_URL}/api/ads/${ad.ad_id}/invoice`} target="_blank" rel="noreferrer"><Button variant="outline" className="h-8" data-testid={`ad-invoice-${ad.ad_id}`}>Invoice</Button></a>{canManage && ad.status === "active" && <Button variant="outline" className="h-8 border-red-300 text-red-700" onClick={() => stopAd(ad.ad_id)} data-testid={`ad-stop-${ad.ad_id}`}>Stop</Button>}</td>
            </tr>) : <tr><td className="p-6 text-center text-[#475569]" colSpan="6">No advertisements yet.</td></tr>}</tbody>
          </table>
        </div>
      </div>
    </Card>
  );
  const statementsCard = (
    <Card className="neumorphic-card rounded-3xl mt-6 p-7" data-testid="operator-statements-panel">
      <h2 className="text-2xl font-bold">Statements</h2>
      {status.role === "agent" && <p className="mt-1 text-sm text-[#475569]">Statements are limited to tickets you sold.</p>}
      <div className="grid gap-3 md:grid-cols-5 mt-4">
        <Input type="date" value={statementFilters.start} onChange={(e)=>setStatementFilters({...statementFilters,start:e.target.value})} data-testid="statement-start-date"/>
        <Input type="date" value={statementFilters.end} onChange={(e)=>setStatementFilters({...statementFilters,end:e.target.value})} data-testid="statement-end-date"/>
        <Input placeholder="Counter ID (optional)" value={statementFilters.counter_id} onChange={(e)=>setStatementFilters({...statementFilters,counter_id:e.target.value})} data-testid="statement-counter-id"/>
        {status.role !== "agent" && <Input placeholder="Agent ID (optional)" value={statementFilters.agent_id} onChange={(e)=>setStatementFilters({...statementFilters,agent_id:e.target.value})} data-testid="statement-agent-id"/>}
        <select value={statementFilters.payment_status} onChange={(e)=>setStatementFilters({...statementFilters,payment_status:e.target.value})} className="border rounded-md px-3" data-testid="statement-payment-status"><option value="">All payment statuses</option><option value="completed">Completed</option><option value="pending">Pending</option><option value="failed">Failed</option><option value="rejected">Rejected</option></select>
        {status.role !== "agent" && <select value={statementFilters.park_name} onChange={(e)=>setStatementFilters({...statementFilters,park_name:e.target.value})} className="border rounded-md px-3" data-testid="statement-park-filter"><option value="">All parks (redemptions)</option>{parks.map((p)=><option key={p.park_id} value={p.park_id}>{p.park_id}</option>)}</select>}
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <Button onClick={()=>downloadStatement("sales")} className="bg-[#0EA5E9]" data-testid="download-sales-statement">Download sales CSV</Button>
        <Button variant="outline" onClick={()=>downloadStatement("redemptions")} data-testid="download-redemption-statement">Download redemptions CSV</Button>
      </div>
    </Card>
  );
  return <div className="min-h-screen py-10 px-4"><div className="max-w-6xl mx-auto"><div className="flex justify-between gap-4 items-center"><h1 className="text-4xl font-black text-[#0F172A]">{title}</h1><Button variant="outline" onClick={logout} data-testid="operator-logout-button">Sign out</Button></div>{status.role === "agent" ? <Card className="neumorphic-card rounded-3xl mt-8 p-7"><h2 className="text-2xl font-bold">Agent Wallet</h2><p className="mt-2">Wallet balance: <strong>₹{Number(status.wallet_balance || 0).toFixed(2)}</strong></p><p className="mt-1">Membership: {status.membership_tier || "Choose this month"} · {status.workspace_locked ? "Workspace locked" : "Active"}</p><div className="mt-5 flex gap-3"><select value={tier} onChange={(e) => setTier(e.target.value)} className="border rounded-md px-3" data-testid="agent-membership-tier-select"><option value="gold">Gold — ₹800/month, 29% total MRP discount</option><option value="silver">Silver — ₹500/month, 27.5% total MRP discount</option></select><Button onClick={chooseMembership} className="bg-[#0EA5E9]" data-testid="agent-choose-membership-button">Choose membership</Button></div><div className="mt-6 border-t pt-5" data-testid="agent-wallet-topup"><h3 className="text-xl font-bold">Add money to wallet</h3><p className="mt-1 text-sm text-[#475569]">Top up any amount securely with Razorpay. Funds are added instantly after payment.</p><div className="mt-3 flex flex-wrap gap-2">{[500, 800, 1000, 2000].map((amt) => <Button key={amt} variant="outline" onClick={() => setTopupAmount(String(amt))} data-testid={`agent-wallet-quick-${amt}`}>₹{amt}</Button>)}</div><div className="mt-3 flex flex-wrap gap-3 items-center"><Input type="number" min="1" step="0.01" placeholder="Enter amount ₹" value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)} className="max-w-[220px]" data-testid="agent-wallet-amount-input" /><Button onClick={topUpWallet} disabled={topupBusy || !Razorpay} className="bg-[#0F766E] hover:bg-[#0d655e]" data-testid="agent-wallet-pay-button">{topupBusy ? "Opening Razorpay…" : `Pay ₹${(Number(topupAmount) || 0).toFixed(2)}`}</Button></div></div>{status.renewal_due && <div className="mt-6 rounded-2xl border border-red-300 bg-red-50 p-5" data-testid="agent-renewal-banner"><p className="font-bold text-red-800">Membership renewal due{status.renewal_amount ? ` · ₹${Number(status.renewal_amount).toFixed(2)}` : ""}</p><p className="mt-1 text-sm text-red-700">{autopayMethod === "wallet" ? "Top up your wallet and autopay will renew it automatically, or pay now with Razorpay." : "Tap below to auto-pay this month's fee with Razorpay."}</p><Button onClick={renewViaRazorpay} disabled={topupBusy || !Razorpay} className="mt-3 bg-[#0F766E] hover:bg-[#0d655e]" data-testid="agent-renew-now-button">{topupBusy ? "Opening Razorpay…" : "Pay now with Razorpay"}</Button></div>}<div className="mt-6 border-t pt-5" data-testid="agent-autopay-panel"><h3 className="text-xl font-bold">Autopay</h3><p className="mt-1 text-sm text-[#475569]">Keep your membership active automatically every month.</p><label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={autopayOn} onChange={(e) => setAutopayOn(e.target.checked)} data-testid="agent-autopay-enabled" /> Enable autopay</label><div className="mt-3 flex flex-wrap gap-3 items-center"><select value={autopayMethod} onChange={(e) => setAutopayMethod(e.target.value)} className="border rounded-md px-3 h-10" data-testid="agent-autopay-method"><option value="wallet">Auto-deduct from wallet</option><option value="razorpay">Auto-charge via Razorpay</option></select><Button onClick={saveAutopay} className="bg-[#0EA5E9]" data-testid="agent-autopay-save">Save autopay</Button></div>{status.membership_next_due && <p className="mt-2 text-xs text-[#475569]">Next renewal: {new Date(status.membership_next_due).toLocaleDateString()}</p>}</div></Card> : <>{productCard}{canManage && eposCard}{canManage && <Card className="neumorphic-card rounded-3xl mt-6 p-7"><h2 className="text-2xl font-bold">Operations</h2><div className="grid gap-3 md:grid-cols-5 mt-4"><Input placeholder="Name" value={newAccount.display_name} onChange={(e) => setNewAccount({...newAccount, display_name:e.target.value})} data-testid="new-operator-name-input"/><Input placeholder="ID" value={newAccount.operator_id} onChange={(e) => setNewAccount({...newAccount, operator_id:e.target.value})} data-testid="new-operator-id-input"/><Input placeholder="Password" type="password" value={newAccount.password} onChange={(e) => setNewAccount({...newAccount, password:e.target.value})} data-testid="new-operator-password-input"/><select value={newAccount.role} onChange={(e) => setNewAccount({...newAccount, role:e.target.value})} className="border rounded-md px-3" data-testid="new-operator-role-select"><option value="admin">Admin</option><option value="agent">Agent</option></select><Input placeholder="Counter ID (optional)" value={newAccount.counter_id} onChange={(e) => setNewAccount({...newAccount, counter_id:e.target.value.replace(/[^A-Za-z0-9_-]/g, "")})} maxLength={40} data-testid="new-operator-counter-input"/></div><Button onClick={addAccount} className="mt-4 bg-[#0EA5E9]" data-testid="create-operator-account-button">Create account</Button><div className="mt-5 space-y-2">{accounts.map((account) => <div key={account.operator_id} className="flex justify-between"><p className="text-sm">{account.display_name} — {account.operator_id} ({account.role})</p>{["admin","agent"].includes(account.role) && <Button variant="outline" onClick={() => deleteAccount(account)} data-testid={`delete-operator-${account.operator_id}`}>Remove</Button>}</div>)}</div></Card>}{canOversee && agentsOverviewCard}{canOversee && managementCard}</>}{statementsCard}</div></div>;
};

// Login Page
const Login = () => {
  const navigate = useNavigate();

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md"
      >
        <Card className="neumorphic-card rounded-3xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black text-[#0F172A] mb-2" style={{ fontFamily: 'Nunito' }}>
              Welcome Back!
            </h1>
            <p className="text-[#475569]">Login to book your tickets</p>
          </div>

          <Button
            onClick={handleGoogleLogin}
            className="w-full bg-white hover:bg-gray-50 text-[#0F172A] border border-gray-300 text-lg py-6 rounded-full font-bold"
            data-testid="google-login-button"
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5 mr-3" />
            Continue with Google
          </Button>

          <p className="text-center text-sm text-[#475569] mt-6">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </Card>
      </motion.div>
    </div>
  );
};

// Auth Callback
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, {
          withCredentials: true
        });
        setUser(response.data.user);
        toast.success(`Welcome, ${response.data.user.name}!`);
        navigate('/dashboard', { replace: true, state: { user: response.data.user } });
      } catch (error) {
        console.error('Auth error:', error);
        toast.error('Authentication failed');
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0EA5E9] mx-auto mb-4"></div>
        <p className="text-[#475569]">Completing login...</p>
      </div>
    </div>
  );
};

// Dashboard (Protected)
const Dashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, [location]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0EA5E9]"></div>
      </div>
    );
  }

  if (isAuthenticated === false) {
    window.location.href = '/login';
    return null;
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
          <Card className="neumorphic-card rounded-3xl p-8" data-testid="user-dashboard">
            <div className="text-center">
              {user?.picture && (
                <img src={user.picture} alt={user.name} className="w-24 h-24 rounded-full mx-auto mb-4" />
              )}
              <h1 className="text-3xl font-black text-[#0F172A] mb-2" style={{ fontFamily: 'Nunito' }}>
                Welcome, {user?.name}!
              </h1>
              <p className="text-[#475569] mb-6">{user?.email}</p>
              <Button onClick={() => window.location.href = '/'} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
                Start Booking
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

// Verification Card Component for Admin
const VerificationCard = ({ order, onVerified }) => {
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [notes, setNotes] = useState("");

  const aiVerification = order.ai_verification || {};
  const hasAiAnalysis = aiVerification.verified !== undefined;

  const handleVerify = async (action) => {
    if (action === 'reject' && !notes) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setVerifying(true);
    try {
      await axios.post(`${API}/orders/${order.order_id}/verify`, {
        action,
        admin_notes: notes || null
      }, { withCredentials: true });
      
      toast.success("Payment rejected. Customer notified.");
      onVerified();
    } catch (error) {
      console.error("Verification error:", error);
      toast.error(error.response?.data?.detail || "Failed to process verification");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="border-2 border-[#FBBF24] bg-[#FFFBEB] p-6 rounded-xl" data-testid={`verify-order-${order.order_id}`}>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h4 className="font-bold text-[#0F172A] text-lg">{order.customer_name}</h4>
          <p className="text-sm text-[#475569]">{order.customer_email}</p>
          <p className="text-xs text-[#475569]">Order: {order.order_id}</p>
          <p className="text-sm font-bold text-[#0EA5E9] mt-2">Amount: ₹{order.total.toFixed(2)}</p>
        </div>
        <div className="flex space-x-2">
          {hasAiAnalysis && (
            <Button onClick={() => setShowAiAnalysis(!showAiAnalysis)} variant="outline" size="sm" className="bg-purple-50">
              {showAiAnalysis ? "Hide" : "View"} AI Analysis
            </Button>
          )}
          <Button onClick={() => setShowScreenshot(!showScreenshot)} variant="outline" size="sm" data-testid={`view-screenshot-${order.order_id}`}>
            {showScreenshot ? "Hide" : "View"} Screenshot
          </Button>
        </div>
      </div>

      {/* AI Analysis Section */}
      {hasAiAnalysis && (
        <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <h5 className="font-bold text-purple-900 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              AI Verification Result
            </h5>
            <Badge className={aiVerification.verified ? 'bg-green-600' : 'bg-orange-600'}>
              Confidence: {(aiVerification.confidence * 100).toFixed(0)}%
            </Badge>
          </div>
          {showAiAnalysis && aiVerification.analysis && (
            <div className="text-sm space-y-2 mt-3">
              <div className="grid grid-cols-2 gap-2">
                <div><strong>Transaction Status:</strong> {aiVerification.analysis.transaction_status || 'N/A'}</div>
                <div><strong>Amount Match:</strong> {aiVerification.analysis.amount_matches ? '✓ Yes' : '✗ No'}</div>
                <div><strong>UPI Match:</strong> {aiVerification.analysis.upi_matches ? '✓ Yes' : '✗ No'}</div>
                <div><strong>Verdict:</strong> {aiVerification.analysis.overall_verdict || 'N/A'}</div>
              </div>
              <div className="mt-3 p-2 bg-white rounded border">
                <strong>AI Reason:</strong> {aiVerification.analysis.reason || 'No details provided'}
              </div>
            </div>
          )}
        </div>
      )}

      {showScreenshot && order.payment_screenshot && (
        <div className="mb-4 p-4 bg-white rounded-xl border-2 border-gray-200">
          <img src={order.payment_screenshot} alt="Payment proof" className="max-h-96 mx-auto rounded-lg" />
        </div>
      )}

      <div className="mb-4">
        <Label htmlFor={`notes-${order.order_id}`} className="text-sm">Rejection reason</Label>
        <Textarea
          id={`notes-${order.order_id}`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter verification notes..."
          className="mt-2"
          rows={2}
          data-testid={`admin-notes-${order.order_id}`}
        />
      </div>

      <div className="flex space-x-3">
        <Button
          onClick={() => handleVerify('reject')}
          disabled={verifying}
          className="w-full bg-red-600 hover:bg-red-700 text-white"
          data-testid={`reject-payment-${order.order_id}`}
        >
          {verifying ? "Processing..." : "✗ Reject"}
        </Button>
      </div>
      
      {hasAiAnalysis && (
        <p className="text-xs text-purple-700 mt-3 text-center">
          AI approval is the only path to ticket generation. This order needs a clear resubmitted payment screenshot.
        </p>
      )}
    </div>
  );
};

const OwnerAdminPortal = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (user?.admin_role !== "owner") {
      navigate("/");
      return;
    }
    const checkGate = async () => {
      try {
        const response = await axios.get(`${API}/admin/owner-gate/status`, { withCredentials: true });
        setUnlocked(response.data.unlocked);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Unable to verify owner access.");
      } finally {
        setLoading(false);
      }
    };
    checkGate();
  }, [authLoading, navigate, user]);

  const unlockPortal = async () => {
    if (!password) return toast.error("Enter the owner password.");
    setUnlocking(true);
    try {
      await axios.post(`${API}/admin/owner-gate/unlock`, { password }, { withCredentials: true });
      setPassword("");
      setUnlocked(true);
      toast.success("Owner portal unlocked for this session.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to unlock the owner portal.");
    } finally {
      setUnlocking(false);
    }
  };

  if (authLoading || loading) return <div className="min-h-screen flex items-center justify-center" data-testid="owner-gate-loading">Checking owner access…</div>;
  if (!unlocked) {
    return (
      <div className="min-h-screen py-12 px-4 flex items-center justify-center">
        <Card className="neumorphic-card rounded-3xl p-8 max-w-md w-full" data-testid="owner-password-gate">
          <h1 className="text-3xl font-black text-[#0F172A]" style={{ fontFamily: "Nunito" }}>Owner Portal</h1>
          <p className="mt-3 text-[#475569]">Confirm the owner password to manage Friendship Ad for this signed-in session.</p>
          <Label htmlFor="owner-portal-password" className="block mt-6">Owner password</Label>
          <Input id="owner-portal-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} onKeyDown={(event) => event.key === "Enter" && unlockPortal()} className="mt-2" autoComplete="current-password" data-testid="owner-password-input" />
          <Button onClick={unlockPortal} disabled={unlocking} className="mt-5 w-full bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="owner-password-unlock-button">{unlocking ? "Unlocking…" : "Unlock owner portal"}</Button>
        </Card>
      </div>
    );
  }
  return <AdminPanel />;
};

// Owner Admin Panel (Protected)
const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [orders, setOrders] = useState([]);
  const [administrators, setAdministrators] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminName, setNewAdminName] = useState("");
  const [adminSaving, setAdminSaving] = useState(false);
  const [statementMonth, setStatementMonth] = useState(new Date().toISOString().slice(0, 7));
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const refreshMfaStatus = useCallback(async () => {
    const response = await axios.get(`${API}/admin/mfa/status`, { withCredentials: true });
    setMfaStatus(response.data);
    return response.data;
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, couponsRes, ordersRes, administratorsRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/coupons`, { withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true }),
        axios.get(`${API}/admin/administrators`, { withCredentials: true })
      ]);
      setProducts(productsRes.data);
      setCoupons(couponsRes.data);
      setOrders(ordersRes.data);
      setAdministrators(administratorsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  const initializeAdmin = useCallback(async () => {
    setLoading(true);
    try {
      const currentMfaStatus = await refreshMfaStatus();
      if (!currentMfaStatus.enabled || currentMfaStatus.verified_current_session) {
        await fetchData();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error initializing admin:", error);
      toast.error(error.response?.data?.detail || "Failed to open admin dashboard");
      setLoading(false);
    }
  }, [fetchData, refreshMfaStatus]);

  useEffect(() => {
    if (user?.admin_role !== "owner") {
      toast.error("Owner administrator access required");
      navigate('/');
      return;
    }
    initializeAdmin();
  }, [user, navigate, initializeAdmin]);

  const addDelegatedAdmin = async () => {
    if (!newAdminEmail.trim()) return toast.error("Enter the administrator email.");
    setAdminSaving(true);
    try {
      await axios.post(`${API}/admin/administrators`, { email: newAdminEmail.trim(), name: newAdminName.trim() || "Delegated Administrator" }, { withCredentials: true });
      setNewAdminEmail("");
      setNewAdminName("");
      toast.success("Delegated administrator added.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to add administrator.");
    } finally {
      setAdminSaving(false);
    }
  };

  const revokeDelegatedAdmin = async (email) => {
    if (!window.confirm(`Remove ${email} from delegated admin access?`)) return;
    try {
      await axios.delete(`${API}/admin/administrators/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success("Delegated administrator removed.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to remove administrator.");
    }
  };

  const generateStatement = async () => {
    if (!statementMonth) return toast.error("Choose a month first.");
    setStatementLoading(true);
    try {
      const response = await axios.get(`${API}/admin/statements/${statementMonth}`, { withCredentials: true });
      setStatement(response.data);
      toast.success("Monthly statement generated.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to generate this statement.");
    } finally {
      setStatementLoading(false);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      toast.success("Product deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const deleteCoupon = async (couponId) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;
    try {
      await axios.delete(`${API}/coupons/${couponId}`, { withCredentials: true });
      toast.success("Coupon deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete coupon");
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-xl">Loading...</div></div>;
  }

  if (mfaStatus?.enabled && !mfaStatus?.verified_current_session) {
    return (
      <div className="min-h-screen py-12 px-4">
        <div className="mx-auto max-w-3xl">
          <div className="mb-8">
            <h1 className="text-4xl font-black text-[#0F172A]" style={{ fontFamily: 'Nunito' }} data-testid="admin-dashboard">
              Admin Dashboard
            </h1>
            <p className="mt-2 text-sm text-[#475569]">Verify your authenticator code to continue.</p>
          </div>
          <AdminMfaGate API={API} onVerified={initializeAdmin} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-black text-[#0F172A]" style={{ fontFamily: 'Nunito' }} data-testid="admin-dashboard">
            Admin Dashboard
          </h1>
        </div>

        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 md:grid-cols-6">
            <TabsTrigger value="products" data-testid="products-tab">
              <Package className="w-4 h-4 mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger value="coupons" data-testid="coupons-tab">
              <Tag className="w-4 h-4 mr-2" />
              Coupons
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="statements" data-testid="statements-tab">
              <Tag className="w-4 h-4 mr-2" />
              Statements
            </TabsTrigger>
            <TabsTrigger value="administrators" data-testid="administrators-tab">
              <User className="w-4 h-4 mr-2" />
              Admins
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">
              <User className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            <Card className="neumorphic-card rounded-3xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Products</h2>
                <Button onClick={() => { setEditingProduct(null); setShowProductModal(true); }} className="bg-[#0EA5E9]" data-testid="add-product-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </Button>
              </div>
              <div className="space-y-4">
                {products.map((product) => (
                  <div key={product.product_id} className="flex items-center justify-between p-4 bg-[#F0F9FF] rounded-xl" data-testid={`admin-product-${product.product_id}`}>
                    <div className="flex items-center space-x-4">
                      <img src={resolveImageUrl(product.image_url)} alt={product.name} className="w-16 h-16 rounded-lg object-cover" />
                      <div>
                        <h3 className="font-bold text-[#0F172A]">{product.name}</h3>
                        <p className="text-sm text-[#475569]">₹{product.final_price} | Stock: {product.stock}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button onClick={() => { setEditingProduct(product); setShowProductModal(true); }} variant="outline" size="sm">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => deleteProduct(product.product_id)} variant="ghost" size="sm" className="text-[#F43F5E]" data-testid={`delete-product-${product.product_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="coupons">
            <Card className="neumorphic-card rounded-3xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Coupons</h2>
                <Button onClick={() => setShowCouponModal(true)} className="bg-[#0EA5E9]" data-testid="add-coupon-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Coupon
                </Button>
              </div>
              <div className="space-y-4">
                {coupons.map((coupon) => (
                  <div key={coupon.coupon_id} className="flex items-center justify-between p-4 bg-[#F0F9FF] rounded-xl" data-testid={`admin-coupon-${coupon.coupon_id}`}>
                    <div>
                      <h3 className="font-bold text-[#0F172A]">{coupon.code}</h3>
                      <p className="text-sm text-[#475569]">{coupon.discount_percent}% off | Valid until: {new Date(coupon.valid_until).toLocaleDateString()}</p>
                    </div>
                    <Button onClick={() => deleteCoupon(coupon.coupon_id)} variant="ghost" size="sm" className="text-[#F43F5E]" data-testid={`delete-coupon-${coupon.coupon_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="orders">
            <Card className="neumorphic-card rounded-3xl p-6">
              <h2 className="text-2xl font-bold mb-6">Orders & Verifications</h2>
              
              {/* Pending Verifications */}
              {orders.filter(o => o.payment_status === 'pending_verification').length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-bold text-[#F43F5E] mb-4">⚠️ Pending Verification ({orders.filter(o => o.payment_status === 'pending_verification').length})</h3>
                  <div className="space-y-4">
                    {orders.filter(o => o.payment_status === 'pending_verification').map((order) => (
                      <VerificationCard key={order.order_id} order={order} onVerified={fetchData} />
                    ))}
                  </div>
                </div>
              )}
              
              {/* All Orders */}
              <h3 className="text-lg font-bold text-[#0F172A] mb-4">All Orders</h3>
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.order_id} className="p-4 bg-[#F0F9FF] rounded-xl" data-testid={`admin-order-${order.order_id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold text-[#0F172A]">{order.customer_name}</h3>
                        <p className="text-sm text-[#475569]">{order.customer_email}</p>
                        <p className="text-xs text-[#475569] mt-1">Order ID: {order.order_id}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#0EA5E9]">₹{order.total.toFixed(2)}</p>
                        <Badge className={
                          order.payment_status === 'completed' ? 'bg-green-500' :
                          order.payment_status === 'pending_verification' ? 'bg-yellow-500' :
                          order.payment_status === 'rejected' ? 'bg-red-500' : 'bg-gray-500'
                        }>
                          {order.payment_status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="statements">
            <Card className="neumorphic-card rounded-3xl p-6" data-testid="monthly-statements-panel">
              <h2 className="text-2xl font-bold text-[#0F172A]">Monthly Statement</h2>
              <p className="mt-1 text-sm text-[#475569]">Review all customer orders, ticket quantities, total amounts, statuses, and ticket types for any month.</p>
              <div className="mt-6 flex flex-wrap gap-3 items-end">
                <div>
                  <Label htmlFor="statement-month">Statement month</Label>
                  <Input id="statement-month" type="month" value={statementMonth} onChange={(event) => setStatementMonth(event.target.value)} className="mt-2" data-testid="statement-month-input" />
                </div>
                <Button onClick={generateStatement} disabled={statementLoading} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="generate-statement-button">{statementLoading ? "Generating…" : "Generate statement"}</Button>
              </div>
              {statement && (
                <div className="mt-8 space-y-6" data-testid="monthly-statement-result">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="rounded-2xl bg-sky-50 p-4"><p className="text-xs text-[#475569]">All orders</p><p className="text-2xl font-black text-[#0F172A]" data-testid="statement-all-orders">{statement.all_orders_count}</p></div>
                    <div className="rounded-2xl bg-emerald-50 p-4"><p className="text-xs text-[#475569]">Completed orders</p><p className="text-2xl font-black text-[#0F172A]">{statement.orders_count}</p></div>
                    <div className="rounded-2xl bg-amber-50 p-4"><p className="text-xs text-[#475569]">Tickets sold</p><p className="text-2xl font-black text-[#0F172A]">{statement.tickets_sold}</p></div>
                    <div className="rounded-2xl bg-rose-50 p-4"><p className="text-xs text-[#475569]">Collected</p><p className="text-2xl font-black text-[#0F172A]" data-testid="statement-collected">₹{Number(statement.gross_sales).toFixed(2)}</p></div>
                  </div>
                  <div className="grid gap-6 lg:grid-cols-2">
                    <div><h3 className="font-bold text-[#0F172A] mb-3">Order status breakdown</h3><div className="space-y-2">{statement.status_breakdown.map(([status, count, amount]) => <div key={status} className="flex justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm"><span className="capitalize">{status} · {count}</span><strong>₹{Number(amount).toFixed(2)}</strong></div>)}</div></div>
                    <div><h3 className="font-bold text-[#0F172A] mb-3">Ticket type breakdown</h3><div className="space-y-2">{statement.type_breakdown.map(([type, quantity, amount]) => <div key={type} className="flex justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm"><span className="capitalize">{type} · {quantity} tickets</span><strong>₹{Number(amount).toFixed(2)}</strong></div>)}</div></div>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0F172A] mb-3">Customer orders for {statement.period}</h3>
                    <div className="overflow-x-auto rounded-2xl border border-slate-200">
                      <table className="min-w-full text-sm" data-testid="statement-customer-orders-table">
                        <thead className="bg-slate-50 text-left text-[#475569]"><tr><th className="p-3">Customer</th><th className="p-3">Order</th><th className="p-3">Status</th><th className="p-3">Tickets</th><th className="p-3">Amount</th></tr></thead>
                        <tbody>{statement.customer_orders.map((order) => <tr key={order.order_id} className="border-t border-slate-100"><td className="p-3"><p className="font-semibold text-[#0F172A]">{order.customer_name}</p><p className="text-xs text-[#475569]">{order.customer_email}</p></td><td className="p-3">{order.order_id}</td><td className="p-3 capitalize">{order.payment_status}</td><td className="p-3">{order.ticket_quantity}</td><td className="p-3 font-semibold">₹{Number(order.total).toFixed(2)}</td></tr>)}</tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="administrators">
            <Card className="neumorphic-card rounded-3xl p-6" data-testid="administrator-management-panel">
              <h2 className="text-2xl font-bold text-[#0F172A]">Administrator Access</h2>
              <p className="mt-1 text-sm text-[#475569]">Delegated administrators can use the staff QR scanner only. They cannot open this owner portal or change settings.</p>
              <div className="mt-6 grid gap-3 md:grid-cols-[1fr_1fr_auto]">
                <Input value={newAdminName} onChange={(event) => setNewAdminName(event.target.value)} placeholder="Administrator name" data-testid="new-admin-name-input" />
                <Input value={newAdminEmail} onChange={(event) => setNewAdminEmail(event.target.value)} type="email" placeholder="Google account email" data-testid="new-admin-email-input" />
                <Button onClick={addDelegatedAdmin} disabled={adminSaving} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="add-delegated-admin-button">{adminSaving ? "Adding…" : "Add admin"}</Button>
              </div>
              <div className="mt-6 space-y-3">
                {administrators.map((admin) => (
                  <div key={admin.email} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-slate-50 p-4" data-testid={`administrator-${admin.email}`}>
                    <div><p className="font-bold text-[#0F172A]">{admin.name}</p><p className="text-sm text-[#475569]">{admin.email}</p></div>
                    <div className="flex items-center gap-3"><Badge className={admin.admin_role === "owner" ? "bg-[#0EA5E9]" : "bg-slate-600"}>{admin.admin_role}</Badge>{admin.admin_role !== "owner" && <Button onClick={() => revokeDelegatedAdmin(admin.email)} variant="outline" className="text-red-600" data-testid={`revoke-admin-${admin.email}`}>Remove</Button>}</div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card className="neumorphic-card rounded-3xl p-6">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-[#0F172A]">Admin Settings</h2>
                  <p className="text-sm text-[#475569]">Website, domain, Gemini AI, email, and security controls.</p>
                </div>
              </div>
              <AdminSettingsPanel API={API} mfaStatus={mfaStatus} refreshMfaStatus={refreshMfaStatus} />
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Product Modal */}
      <ProductModal
        isOpen={showProductModal}
        onClose={() => { setShowProductModal(false); setEditingProduct(null); }}
        product={editingProduct}
        onSuccess={() => { setShowProductModal(false); setEditingProduct(null); fetchData(); }}
      />

      {/* Coupon Modal */}
      <CouponModal
        isOpen={showCouponModal}
        onClose={() => setShowCouponModal(false)}
        onSuccess={() => { setShowCouponModal(false); fetchData(); }}
      />
    </div>
  );
};

// Product Modal Component
const ProductModal = ({ isOpen, onClose, product, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    mrp: '',
    discount_percent: '20',
    stock: '1000',
    image_url: ''
  });

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        description: product.description,
        mrp: product.mrp.toString(),
        discount_percent: product.discount_percent.toString(),
        stock: product.stock.toString(),
        image_url: product.image_url || ''
      });
    } else {
      setFormData({ name: '', description: '', mrp: '', discount_percent: '20', stock: '1000', image_url: '' });
    }
  }, [product]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        mrp: parseFloat(formData.mrp),
        discount_percent: parseFloat(formData.discount_percent),
        stock: parseInt(formData.stock)
      };

      if (product) {
        await axios.put(`${API}/products/${product.product_id}`, data, { withCredentials: true });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/products`, data, { withCredentials: true });
        toast.success("Product created");
      }
      onSuccess();
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{product ? 'Edit Product' : 'Add New Product'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Product Name</Label>
            <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="product-name-input" />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required data-testid="product-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="mrp">MRP (₹)</Label>
              <Input id="mrp" type="number" min="0.01" step="0.01" value={formData.mrp} onChange={(e) => setFormData({...formData, mrp: e.target.value})} required data-testid="product-mrp-input" />
            </div>
            <div>
              <Label htmlFor="discount">Discount (%)</Label>
              <Input id="discount" type="number" min="0" max="100" step="0.01" value={formData.discount_percent} onChange={(e) => setFormData({...formData, discount_percent: e.target.value})} required data-testid="product-discount-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="stock">Stock</Label>
            <Input id="stock" type="number" value={formData.stock} onChange={(e) => setFormData({...formData, stock: e.target.value})} required data-testid="product-stock-input" />
          </div>
          <div>
            <Label htmlFor="image_url">Image URL</Label>
            <Input id="image_url" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} data-testid="product-image-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-[#0EA5E9]" data-testid="save-product-button">{product ? 'Update' : 'Create'} Product</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Coupon Modal Component
const CouponModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    code: '',
    discount_percent: '',
    valid_until: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        code: formData.code.toUpperCase(),
        discount_percent: parseFloat(formData.discount_percent),
        valid_until: new Date(formData.valid_until).toISOString()
      };

      await axios.post(`${API}/coupons`, data, { withCredentials: true });
      toast.success("Coupon created");
      setFormData({ code: '', discount_percent: '', valid_until: '' });
      onSuccess();
    } catch (error) {
      console.error("Error creating coupon:", error);
      toast.error(error.response?.data?.detail || "Failed to create coupon");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Coupon</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="code">Coupon Code</Label>
            <Input id="code" value={formData.code} onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})} required data-testid="coupon-code-input" />
          </div>
          <div>
            <Label htmlFor="discount">Discount (%)</Label>
            <Input id="discount" type="number" value={formData.discount_percent} onChange={(e) => setFormData({...formData, discount_percent: e.target.value})} required data-testid="coupon-discount-input" />
          </div>
          <div>
            <Label htmlFor="valid_until">Valid Until</Label>
            <Input id="valid_until" type="date" value={formData.valid_until} onChange={(e) => setFormData({...formData, valid_until: e.target.value})} required data-testid="coupon-date-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-[#0EA5E9]" data-testid="save-coupon-button">Create Coupon</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Router
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment/:orderId" element={<Payment />} />
        <Route path="/tickets/:orderId" element={<Tickets />} />
        <Route path="/my-orders" element={<CustomerOrderHistory />} />
        <Route path="/scan" element={<ParkPortal />} />
        <Route path="/park" element={<ParkPortal />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/advertise" element={<AdvertisePage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/owner" element={<OperatorPortal expectedRole="owner" title="Owner Workspace" />} />
        <Route path="/chief-admin" element={<OperatorPortal expectedRole="chief_admin" title="Chief Admin Workspace" />} />
        <Route path="/admin" element={<OperatorPortal expectedRole="admin" title="Admin Workspace" />} />
        <Route path="/agent-workspace" element={<OperatorPortal expectedRole="agent" title="Agent Workspace" />} />
      </Routes>
      <SiteFooter />
      <Chatbot />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <Toaster position="top-center" />
          <AppRouter />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
